<template>
  <div class="cesium-container"> 
    <div v-if="areaLoading" class="buffer-loading-overlay">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
          <div class="loading-text">计算中，请稍候...</div>
          <!-- 新增退出按钮 -->
          <button class="loading-cancel-btn" @click="cancelAreaLoading">
            <i class="fas fa-times"></i> 退出计算
          </button>
        </div>
      </div>


    <!-- 区域上传模态框 -->
    <div v-if="isAreaUploadModalVisible" class="area-upload-modal">
      <div class="area-upload-content">
        <div class="upload-header">
          <h3>上传区域文件</h3>
          <button class="close-button" @click="closeAreaUploadModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="upload-body">
          <div class="form-group">
            <label>区域名称</label>
            <input v-model="newAreaData.name" placeholder="请输入区域名称">
          </div>
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="newAreaData.description" placeholder="请输入区域描述"></textarea>
          </div>
          <div class="form-group">
            <label>区域文件 (Shp)</label>
            <input 
              type="file" 
              ref="areaFileInput"
              accept=".geojson,.json,.zip"
              @change="handleAreaFileChange"
            >
            <div v-if="areaFilePreview" class="file-preview">
              <i class="fas fa-file-alt"></i>
              <span>{{ areaFilePreview.name }}</span>
              <span>({{ formatFileSize(areaFilePreview.size) }})</span>
            </div>
          </div>
          <div class="upload-actions">
            <button class="upload-button" @click="uploadArea" :disabled="!areaFile">
              上传
            </button>
            <button class="cancel-button" @click="closeAreaUploadModal">
              取消
            </button>
          </div>
        </div>
      </div>
    </div>


    <!-- 清除控制面板 -->
    <div v-if="showMarkerClearButton || showTrackClearButton || showAreaClearButton" class="clear-control-panel">
      <button 
        v-if="showMarkerClearButton" 
        @click="clearCurrentMarker" 
        class="clear-btn clear-marker-btn"
      >
        <i class="fas fa-map-marker-alt"></i> 清除标记
      </button>
      
      <button 
        v-if="showTrackClearButton" 
        @click="clearCurrentTrack" 
        class="clear-btn clear-track-btn"
      >
        <i class="fas fa-route"></i> 清除轨迹
      </button>


      <button 
        v-if="showAreaClearButton" 
        @click="clearCurrentArea" 
        class="clear-btn clear-area-btn"
      >
        <i class="fas fa-route"></i> 清除区域
      </button>
    </div>


    <!-- 地质年代信息卡片 -->
    <div class="stratum-info-card" :class="{ active: currentStratumInfo }">
      <div class="card-header">
        <h4>地质年代信息</h4>
        <button @click="clearStratumInfo" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="card-body">
        <div v-if="currentStratumInfo" class="stratum-content">
          <div class="stratum-header">
            <div class="stratum-name">{{ currentStratumInfo.qduecd }}</div>
            <div class="stratum-tong">{{ currentStratumInfo.tong }}</div>
          </div>
          
          <div class="stratum-period">
            <div class="period-item">
              <span class="period-label">起始时间</span>
              <span class="period-value">{{ currentStratumInfo.lower_age }} Ma</span>
            </div>
            <div class="period-item">
              <span class="period-label">结束时间</span>
              <span class="period-value">{{ currentStratumInfo.upper_age }} Ma</span>
            </div>
          </div>
          
          <div class="stratum-description">
            <p>该{{ currentStratumInfo.qduecd.includes('组') ? '地层' : '岩体' }}形成于
              <span class="highlight">{{ currentStratumInfo.lower_age }}</span> 至
              <span class="highlight">{{ currentStratumInfo.upper_age }}</span> 百万年前，
              属于地质年代的 <span class="highlight">{{ currentStratumInfo.tong }}</span>。
            </p>
          </div>
        </div>
        
        <div v-else class="empty-state">
          <i class="fas fa-layer-group"></i>
          <p>点击地层查看详细信息</p>
        </div>
      </div>
    </div>


    <button @click="toggleGeologyDetail" class="geology-detail-icon-button" :class="{ active: isGeologyDetailActive }" title="地质详情">
      <i class="fas fa-image"></i>
    </button>
    <!-- 在template中添加 -->
    <div v-if="showImageModal" class="image-modal" @click.self="closeImageModal">
        <div class="image-modal-content">
            <button @click="closeImageModal" class="close-button">
                <i class="fas fa-times"></i>
            </button>
            <img :src="currentImageUrl" alt="地质图片" class="modal-image">
            <div class="image-info">地质图块ID: {{ currentFfid }}</div>
        </div>
    </div>
    <!-- 新增：缓冲区地质查询模态框 -->
    <div v-if="showBufferModal" class="buffer-modal" @click.self="closeBufferModal">
      <div class="buffer-content">
        <div class="buffer-header">
          <h4>缓冲区地质查询</h4>
          <button @click="closeBufferModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="buffer-body">
          <div class="form-group">
            <label>缓冲区距离（米）</label>
            <input 
              type="number" 
              v-model="bufferDistance" 
              min="100" 
              max="5000" 
              step="100"
              placeholder="输入缓冲区距离"
            >
          </div>
          <div class="buffer-actions">
            <button class="confirm-btn" @click="loadGeologyByTrackBuffer" :disabled="bufferLoading">
              <span v-if="!bufferLoading">查询地质概况</span>
              <span v-else>计算中...</span>
            </button>
            <button class="cancel-btn" @click="closeBufferModal">
              取消
            </button>
          </div>
        </div>
      </div>
      
      <!-- 新增：加载提示 -->
      <div v-if="bufferLoading" class="buffer-loading-overlay">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
          <div class="loading-text">计算中，请稍候...</div>
          <!-- 新增退出按钮 -->
          <button class="loading-cancel-btn" @click="cancelBufferLoading">
            <i class="fas fa-times"></i> 退出计算
          </button>
        </div>
      </div>
    </div>


  <!-- 修改地层列表项显示 -->
    <div v-if="qduecdList.length > 0" class="qduecd-sidebar">
      <div class="sidebar-header">
        <h4>地层/岩体列表（{{ qduecdList.length }}项）</h4>
        <button @click="clearQduecdList" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="qduecd-list">
        <div 
          v-for="item in qduecdList" 
          :key="item.compositeKey" 
          class="qduecd-item"
          :class="{ active: item.compositeKey === selectedQduecd }"
          @click="handleQduecdClick(item)"
        >
          <div class="stratum-name">{{ item.qduecd }}</div>
          <div class="stratum-tong">{{ item.tong }}</div>
        </div>
      </div>
    </div>

    <!-- 用户头像按钮 -->
    <div class="user-avatar-container">
      <div v-if="!isLoggedIn" class="user-avatar" @click="showLoginModal = true">
        <i class="fas fa-user-circle"></i>
      </div>
      <div v-else class="user-avatar logged-in" @click="toggleUserMenu">
        <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="用户头像" />
        <i v-else class="fas fa-user-circle"></i>
        <div v-if="showUserMenu" class="user-menu">
          <div class="menu-item" @click="logout">退出登录</div>
          
          <!-- 轨迹和标记点选项卡 -->
          <div class="poi-tabs">
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'poi' }"
              @click.stop="activeTab = 'poi'"
            >
              标记点
            </button>
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'track' }"
              @click.stop="activeTab = 'track'"
            >
              轨迹
            </button>

            <!-- 新增区域选项卡 -->
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'area' }"
              @click.stop="activeTab = 'area'"
            >
              区域
            </button>
          </div>
          
          <!-- 标记点列表区域 -->
          <div v-show="activeTab === 'poi'" class="poi-list-container">
            <div class="poi-search-box" @click.stop>
              <input 
                v-model="poiSearchKeyword" 
                @keyup.enter="searchUserPois" 
                @click.stop
                placeholder="搜索我的标记点..."
                class="poi-search-input"
              />
              <button @click.stop="searchUserPois" class="poi-search-button">
                <i class="fas fa-search"></i>
              </button>
              <button 
                v-if="poiSearchKeyword" 
                @click.stop="clearPoiSearch" 
                class="poi-search-clear"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <h4>我的标记点 <span class="total-count">(共{{ poiPagination.total }}条)</span></h4>
            
            <!-- 加载状态 -->
            <div v-if="userPoisLoading" class="loading-state">
              <i class="fas fa-spinner fa-spin"></i> 加载中...
            </div>
            
            <!-- 空状态 -->
            <div v-else-if="userPois.length === 0" class="empty-state">
              <i class="fas fa-map-marker-alt"></i>
              <p>暂无标记点</p>
            </div>
            
            <!-- 列表内容 -->
            <div v-else class="poi-list">
              <div 
                v-for="poi in userPois" 
                :key="poi.id" 
                class="poi-item"
                @click="flyToUserPoi(poi)"
              >
                <div class="poi-header">
                  <div class="poi-name">{{ poi.name || '未命名标记' }}</div>
                  <button 
                    class="delete-btn" 
                    @click.stop="deletePoi(poi)"
                    title="删除标记点"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
                <div class="poi-desc">{{ poi.description || '无描述' }}</div>
                <div v-if="poi.filePath" class="poi-image-preview">
                  <img :src="getImageUrl(poi.filePath)" 
                      alt="标记点图片"
                      @error="handlePoiImageError(poi)"
                      @click.stop="previewPoiImage(poi)">
                </div>
              </div>
            </div>
            
            <!-- 分页控件 -->
            <div class="pagination-footer" v-if="poiPagination.totalPages > 1">
              <div class="pagination-controls">
                <button class="page-btn first" 
                        @click.stop="changePage(1)"
                        :disabled="poiPagination.currentPage === 1">
                  首
                </button>

                <button class="page-btn prev" 
                        @click.stop="changePage(poiPagination.currentPage - 1)"
                        :disabled="poiPagination.currentPage === 1">
                  &lt;
                </button>

                <!-- 只显示当前页码的前后两页 -->
                <button v-for="page in displayedPages" 
                        :key="page"
                        class="page-btn"
                        :class="{ 
                          active: page === poiPagination.currentPage
                        }"
                        @click.stop="changePage(page)"
                        :disabled="page === '...'">
                  {{ page }}
                </button>

                <button class="page-btn next" 
                        @click.stop="changePage(poiPagination.currentPage + 1)"
                        :disabled="poiPagination.currentPage === poiPagination.totalPages">
                  &gt;
                </button>

                <button class="page-btn last" 
                        @click.stop="changePage(poiPagination.totalPages)"
                        :disabled="poiPagination.currentPage === poiPagination.totalPages">
                  末
                </button>
              </div>
            </div>
          </div>
          
          <!-- 轨迹列表区域 -->
          <div v-show="activeTab === 'track'" class="track-list-container">
            <!-- 轨迹搜索框 -->
            <div class="track-search-box" @click.stop>
              <input 
                v-model="trackSearchKeyword" 
                @keyup.enter="searchUserTracks" 
                @click.stop
                placeholder="搜索我的轨迹..."
                class="track-search-input"
              />
              <button @click.stop="searchUserTracks" class="poi-search-button">
                <i class="fas fa-search"></i>
              </button>
              <button 
                v-if="trackSearchKeyword" 
                @click.stop="clearTrackSearch" 
                class="poi-search-clear"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <!-- 轨迹列表标题 -->
            <h4>我的轨迹 <span class="total-count">(共{{ trackPagination.total }}条)</span></h4>
            
            <!-- 加载状态 -->
            <div v-if="userTracksLoading" class="loading-state">
              <i class="fas fa-spinner fa-spin"></i> 加载中...
            </div>
            
            <!-- 空状态 -->
            <div v-else-if="userTracks.length === 0" class="empty-state">
              <i class="fas fa-route"></i>
              <p>暂无轨迹</p>
            </div>
            
            <!-- 轨迹列表 -->
            <div v-else class="track-list">
              <div 
                v-for="track in userTracks" 
                :key="track.id" 
                class="track-item"
                @click="flyToTrack(track)"
              >
                <div class="track-header">
                  <div class="track-name">{{ track.name || '未命名轨迹' }}</div>
                  <button 
                    class="bufferinfo-btn" 
                    @click.stop="showBufferForTrack(track)"
                    title="缓冲区地质查询"
                  >
                    <i class="fas fa-info-circle"></i>
                  </button>
                  <button 
                    class="delete-btn" 
                    @click.stop="deleteTrack(track)"
                    title="删除轨迹"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
                <div class="track-desc">{{ track.description || '无描述' }}</div>
                
                <!-- 起始时间和结束时间分两行显示 -->
                <div class="track-time">
                  <div class="time-row">
                    <span class="time-label">开始：</span>
                    <span class="time-value">{{ formatTime(track.startTime) }}</span>
                  </div>
                  <div class="time-row">
                    <span class="time-label">结束：</span>
                    <span class="time-value">{{ formatTime(track.endTime) }}</span>
                  </div>
                </div>
                
                <div class="track-info">
                  <span class="time-label">里程：</span>
                  <span class="time-value">{{ (track.distance / 1000).toFixed(2) }} km</span>
                </div>

                <div class="track-info">
                  <span class="time-label">高程：</span>
                  <span class="time-value">{{ track.minAltitude.toFixed(0) }}~{{ track.maxAltitude.toFixed(0) }} m</span>
                </div>
              </div>
            </div>
            
            <!-- 分页控件 -->
            <div class="pagination-footer" v-if="trackPagination.totalPages > 1">
              <div class="pagination-controls">
                <button class="page-btn first" 
                        @click.stop="changeTrackPage(1)"
                        :disabled="trackPagination.currentPage === 1">
                  首
                </button>
                <button class="page-btn prev" 
                        @click.stop="changeTrackPage(trackPagination.currentPage - 1)"
                        :disabled="trackPagination.currentPage === 1">
                  &lt;
                </button>
                <button v-for="page in displayedTrackPages" 
                        :key="page"
                        class="page-btn"
                        :class="{ 
                          active: page === trackPagination.currentPage
                        }"
                        @click.stop="changeTrackPage(page)"
                        :disabled="page === '...'">
                  {{ page }}
                </button>
                <button class="page-btn next" 
                        @click.stop="changeTrackPage(trackPagination.currentPage + 1)"
                        :disabled="trackPagination.currentPage === trackPagination.totalPages">
                  &gt;
                </button>
                <button class="page-btn last" 
                        @click.stop="changeTrackPage(trackPagination.totalPages)"
                        :disabled="trackPagination.currentPage === trackPagination.totalPages">
                  末
                </button>
              </div>
            </div>
            
            <!-- 上传轨迹按钮 -->
            <button @click="showTrackUploadModal($event)" class="add-track-btn">
              <i class="fas fa-plus"></i> 上传轨迹
            </button>
          </div>

          <!-- 新增：区域列表区域 -->
          <div v-show="activeTab === 'area'" class="area-list-container">
            <!-- 区域搜索框 -->
            <div class="area-search-box" @click.stop>
              <input 
                v-model="areaSearchKeyword" 
                @keyup.enter="searchUserAreas" 
                @click.stop
                placeholder="搜索我的区域..."
                class="area-search-input"
              />
              <button @click.stop="searchUserAreas" class="poi-search-button">
                <i class="fas fa-search"></i>
              </button>
              <button 
                v-if="areaSearchKeyword" 
                @click.stop="clearAreaSearch" 
                class="poi-search-clear"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <!-- 修改后 (黑色) -->
            <h4 class="polygon-list-title">我的区域 <span class="total-count">(共{{ areaPagination.total }}个)</span></h4>
            
            <!-- 加载状态 -->
            <div v-if="userAreasLoading" class="loading-state">
              <i class="fas fa-spinner fa-spin"></i> 加载中...
            </div>
            
            <!-- 空状态 -->
            <div v-else-if="userAreas.length === 0" class="empty-state">
              <i class="fas fa-draw-polygon"></i>
              <p>暂无区域</p>
            </div>
            
            <!-- 修改区域列表部分 -->
            <div v-else class="area-list">
                <div 
                  v-for="area in userAreas" 
                  :key="area.groupId" 
                  class="area-item"
                  @click="loadAreaGroup(area)"
                >
                  <div class="area-header">
                    <div class="area-name">{{ area.polygonName || '未命名区域' }}</div>
                    <div class="area-actions">
                      <!-- 地质详情按钮 -->
                      <button 
                        class="info-btn" 
                        @click.stop="loadGeologyByPolygon(area)"
                        title="地质详情"
                      >
                        <i class="fas fa-info-circle"></i>
                      </button>
                      <!-- 删除按钮 -->
                      <button 
                        class="delete-btn" 
                        @click.stop="deleteArea(area)"
                        title="删除区域"
                      >
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                  <div class="area-desc">{{ area.description || '无描述' }}</div>
                  
                  <!-- 创建时间和区域类型 -->
                  <div class="area-info">
                    <span class="info-label">创建时间: </span>
                    <span class="info-value">{{ formatTime(area.createTime) }}</span>
                  </div>
                </div>
            </div>
            
            <!-- 分页控件 -->
            <div class="pagination-footer" v-if="areaPagination.totalPages > 1">
              <div class="pagination-controls">
                <button class="page-btn first" 
                        @click.stop="changeAreaPage(1)"
                        :disabled="areaPagination.currentPage === 1">
                  首
                </button>
                <button class="page-btn prev" 
                        @click.stop="changeAreaPage(areaPagination.currentPage - 1)"
                        :disabled="areaPagination.currentPage === 1">
                  &lt;
                </button>
                <button v-for="page in displayedAreaPages" 
                        :key="page"
                        class="page-btn"
                        :class="{ 
                          active: page === areaPagination.currentPage
                        }"
                        @click.stop="changeAreaPage(page)"
                        :disabled="page === '...'">
                  {{ page }}
                </button>
                <button class="page-btn next" 
                        @click.stop="changeAreaPage(areaPagination.currentPage + 1)"
                        :disabled="areaPagination.currentPage === areaPagination.totalPages">
                  &gt;
                </button>
                <button class="page-btn last" 
                        @click.stop="changeAreaPage(areaPagination.totalPages)"
                        :disabled="areaPagination.currentPage === areaPagination.totalPages">
                  末
                </button>
              </div>
            </div>
            
            <!-- 添加区域按钮 -->
            <button @click="showAreaUploadModal($event)" class="add-area-btn">
              <i class="fas fa-plus"></i> 添加区域
            </button>
          </div>

          
        </div>
      </div>
    </div>

    <!-- 登录模态框 -->
    <div v-if="showLoginModal" class="login-modal">
      <div class="login-content">
        <!-- 模态框头部 -->
        <div class="login-header">
          <h3>
            <span v-if="activeForm === 'mailLogin'">邮箱登录</span>
            <span v-else-if="activeForm === 'register'">用户注册</span>
            <span v-else>用户登录</span>
          </h3>
          <button class="close-button" @click="closeLoginModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- 邮箱登录表单 -->
        <div v-if="activeForm === 'mailLogin'" class="mail-login-body">
          <div class="form-group">
            <label>邮箱</label>
            <input 
              type="email" 
              v-model="mailLoginForm.email" 
              placeholder="请输入邮箱"
              :disabled="isSendingCaptcha"
            >
          </div>
          <div class="form-group captcha-group">
            <label>验证码</label>
            <div class="captcha-input">
              <input 
                v-model="mailLoginForm.verifyCode" 
                placeholder="请输入验证码"
                @keyup.enter="handleMailLogin"
              >
              <button
                class="send-captcha-btn"
                :class="{ disabled: captchaCountdown > 0 }"
                @click="sendMailCaptcha"
                :disabled="captchaCountdown > 0 || isSendingCaptcha"
              >
                {{ captchaCountdown > 0 ? `${captchaCountdown}秒后重发` : '获取验证码' }}
              </button>
            </div>
          </div>
          <button class="login-button" @click="handleMailLogin">登录</button>
          <div class="switch-form">
            <a @click.stop="activeForm = 'login'">返回登录</a>
          </div>
        </div>

        <!-- 登录表单 -->
        <div v-else-if="activeForm === 'login'" class="login-body">
          <div class="form-group">
            <label>用户名</label>
            <input 
              type="text" 
              v-model="loginForm.userAccount" 
              placeholder="请输入用户名"
            />
          </div>
          <div class="form-group">
            <label>密码</label>
            <input 
              type="password" 
              v-model="loginForm.userPassword" 
              placeholder="请输入密码"
              @keyup.enter="handleLogin"
            />
          </div>
          <button class="login-button" @click="handleLogin">登录</button>

          <!-- 在切换表单处添加邮箱登录入口 -->
          <div class="login-options">
            <a @click.stop="activeForm = 'mailLogin'">邮箱验证码登录</a>
          </div>
          
          <!-- 注册入口 -->
          <div class="switch-form">
            没有账号？<a @click.stop="activeForm = 'register'">立即注册</a>
          </div>
        </div>

        <!-- 注册表单 -->
        <div v-else-if="activeForm === 'register'" class="register-body">
          <div class="form-group">
            <label>用户名</label>
            <input v-model="registerForm.userAccount" placeholder="请输入用户名"/>
          </div>
          <div class="form-group">
            <label>密码</label>
            <input 
              type="password" 
              v-model="registerForm.userPassword" 
              placeholder="请输入密码"
            />
          </div>
          <div class="form-group">
            <label>确认密码</label>
            <input 
              type="password" 
              v-model="registerForm.checkPassword" 
              placeholder="请再次输入密码"
            />
          </div>
          <div class="form-group">
            <label>手机号</label>
            <input v-model="registerForm.phoneNumber" placeholder="请输入手机号"/>
          </div>
          <!-- 新增邮箱输入 -->
          <div class="form-group">
            <label>邮箱</label>
            <input 
              type="email" 
              v-model="registerForm.email" 
              placeholder="请输入邮箱"
            />
          </div>
          <div class="form-group">
            <label>昵称</label>
            <input v-model="registerForm.userName" placeholder="请输入昵称"/>
          </div>
          <button class="register-button" @click="handleRegister">注册</button>
          <div class="switch-form">
            已有账号？<a @click.stop="activeForm = 'login'">返回登录</a>
          </div>
        </div>
      </div>
    </div>

    <!-- 搜索框 -->
    <div class="search-container">
      <div class="search-box">
        <input 
          v-model="searchKeyword" 
          @keyup.enter="searchPoi" 
          placeholder="输入POI关键词..."
          class="search-input"
        />
        <button @click="searchPoi" class="search-button">
          <i class="fas fa-search"></i>
        </button>
      </div>
      <div v-if="poiResults.length > 0" class="poi-results">
        <div class="poi-result-header">
          <h3>搜索结果 (共{{ poiResults.length }}条)</h3>
          <button @click="clearSearch" class="clear-button">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div 
          v-for="(poi, index) in poiResults.slice(0, 6)" 
          :key="poi.id" 
          class="poi-item"
          @click="flyToPoi(poi)"
        >
          <div class="poi-name">{{ poi.name }}</div>
          <div class="poi-info">
            <div v-if="poi.address" class="poi-address">
              <i class="fas fa-map-marker-alt"></i> {{ poi.address }}
            </div>
            <div class="poi-location">
              <span>坐标: {{ poi.location }}</span>
            </div>
          </div>
          <div v-if="poi.photos && poi.photos.length > 0" class="poi-photo-preview">
            <img :src="poi.photos[0].url" alt="POI图片" @click.stop="showPhoto(poi.photos)" />
          </div>
        </div>
      </div>
    </div>

    <!-- POI表单模态框 -->
    <div v-if="showPoiForm" class="poi-form-modal">
      <div class="poi-form-content">
        <div class="form-group">
          <label>名称</label>
          <input v-model="newPoiData.name" placeholder="请输入标记点名称">
        </div>
        <div class="form-group">
          <label>描述</label>
          <textarea v-model="newPoiData.description" placeholder="请输入描述"></textarea>
        </div>
        <div class="form-group">
          <label>经纬度</label>
          <input v-model="newPoiData.lonLat" placeholder="请输入经纬度，格式为：经度,纬度">
        </div>
        <!-- 图片上传部分 -->
        <div class="form-group">
          <label>上传图片</label>
          <input type="file" @change="handleImageUpload" accept="image/*">
          <div v-if="newPoiData.imagePreview" class="image-preview">
            <img :src="newPoiData.imagePreview" alt="预览图" style="max-width: 100px; max-height: 100px;">
            <button @click="removeImage" class="remove-image-button">×</button>
          </div>
        </div>
        <div class="form-actions">
          <button @click="savePoi" class="save-button">保存</button>
          <button @click="cancelAddPoi" class="cancel-button">取消</button>
        </div>
      </div>
    </div>

    <!-- 轨迹上传模态框 -->
    <div v-if="isTrackUploadModalVisible" class="track-upload-modal">
      <div class="track-upload-content">
        <div class="upload-header">
          <h3>上传轨迹文件</h3>
          <button class="close-button" @click="closeTrackUploadModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="upload-body">
          <div class="form-group">
            <label>轨迹名称</label>
            <input v-model="newTrackData.name" placeholder="请输入轨迹名称">
          </div>
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="newTrackData.description" placeholder="请输入轨迹描述"></textarea>
          </div>
          <div class="form-group">
            <label>轨迹文件 (GPX/KML)</label>
            <input 
              type="file" 
              ref="trackFileInput"
              accept=".gpx,.kml"
              @change="handleTrackFileChange"
            >
            <div v-if="trackFilePreview" class="file-preview">
              <i class="fas fa-file-alt"></i>
              <span>{{ trackFilePreview.name }}</span>
              <span>({{ formatFileSize(trackFilePreview.size) }})</span>
            </div>
          </div>
          <div class="upload-actions">
            <button class="upload-button" @click="uploadTrack" :disabled="!trackFile">
              上传
            </button>
            <button class="cancel-button" @click="closeTrackUploadModal">
              取消
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 控制按钮 -->
    <button @click="toggleGeologyPick" class="geology-pick-icon-button" :class="{ active: isGeologyPickActive }" title="地质拾取">
      <i class="fas fa-mountain"></i>
    </button>
    <button @click="startDrawing" class="profile-button" title="高程剖面">
      <i class="fas fa-chart-line"></i>
    </button>
    <!-- 控制按钮部分 -->
    <!-- 修改后的矩形和多边形按钮 -->
    <button @click="toggleDrawingRectangle" class="draw-rectangle-button" :title="isDrawingRectangle ? '退出矩形绘制' : '绘制矩形'">
      <i :class="isDrawingRectangle ? 'fas fa-toggle-on' : 'far fa-square'"></i>
    </button>
    
    <button @click="toggleDrawingPolygon" class="draw-polygon-button" :title="isDrawingPolygon ? '退出多边形绘制' : '绘制多边形'">
      <i :class="isDrawingPolygon ? 'fas fa-toggle-on' : 'fas fa-draw-polygon'"></i>
    </button>
    <button @click="toggleTerrain" class="terrain-toggle-button">
      地形图 <i :class="useTerrainMap ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
    </button>
    <button @click="toggleRoadNetwork" class="road-network-button">
      {{ showRoadNetwork ? '关闭路网' : '显示路网' }}
    </button>
    <button @click="toggleDayNight" class="day-night-toggle">
      {{ isNightMode ? '关闭日照' : '开启日照' }}
    </button>
    <button @click="resetToNorth" class="compass-button">
      <i class="fas fa-compass"></i>
    </button>
    <button @click="startAddPoiMode" class="add-poi-icon-button" title="添加标记点">
      <i class="fas fa-plus"></i>
    </button>

    <!-- 坐标拾取 -->
    <button 
      @click="toggleCoordinatePick" 
      class="coordinate-pick-button"
      :class="{ active: isCoordinatePickActive }"
    >
      <i class="fas fa-crosshairs"></i> 取坐标
    </button>
    
    <div id="cesiumContainer" ref="cesiumContainer"></div>
    
    <!-- 侧边栏和信息面板 -->
    <div v-if="isGeologyPickActive && geologyData" class="sidebar">
      <div class="geology-data">
        <h3>地质数据</h3>
        <div v-for="(value, key) in geologyData" :key="key">
          <strong>{{ key }}：</strong>{{ value }}
        </div>
        <div v-if="longitude && latitude">
          <strong>经度：</strong>{{ longitude }}<br>
          <strong>纬度：</strong>{{ latitude }}
        </div>
      </div>
    </div>
    <div v-if="showElevationProfile" class="elevation-profile">
      <h3>高程剖面图</h3>
      <button @click="closeElevationProfile" class="close-button">X</button>
      <canvas ref="elevationChart"></canvas>
    </div>
    <div v-if="selectedFeatureInfo" class="feature-info">
      <h3>地质信息</h3>
      <div>岩性/地层: {{ selectedFeatureInfo }}</div>
      <button @click="closeFeatureInfo" class="close-button">X</button>
    </div>

    <!-- 坐标显示面板 -->
    <div v-if="pickedCoordinates" class="coordinate-panel">
      <div class="coordinate-item">
        <label>经度：</label>
        <span>{{ pickedCoordinates.longitude }}</span>
      </div>
      <div class="coordinate-item">
        <label>纬度：</label>
        <span>{{ pickedCoordinates.latitude }}</span>
      </div>
      <div class="coordinate-item">
        <label>高程：</label>
        <span>{{ pickedCoordinates.height }} 米</span>
      </div>
      <button 
        class="copy-button"
        @click="copyCoordinates"
        title="复制坐标"
      >
        <i class="fas fa-copy"></i>
      </button>
    </div>

    <!-- 坐标定位按钮 -->
    <div class="coordinate-trigger" @click="showCoordinateModal = true">
      <i class="fas fa-map-marker-alt"></i>
    </div>

    <!-- 坐标输入模态框 -->
    <div v-if="showCoordinateModal" class="coordinate-modal" @click.self="showCoordinateModal = false">
      <div class="coordinate-input-box">
        <div class="input-header">
          <h4>坐标定位</h4>
          <button class="close-btn" @click="showCoordinateModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="input-body">
          <input 
            ref="coordInput"
            v-model="coordinateInput"
            placeholder="输入经纬度，格式：经度,纬度"
            @keyup.enter="handleCoordinateConfirm"
          />
          <div v-if="inputError" class="error-msg">{{ inputError }}</div>
          <button class="confirm-btn" @click="handleCoordinateConfirm">
            定位
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
  import * as Cesium from "cesium";
  import "cesium/Source/Widgets/widgets.css";
  import { initCesium } from "../common/js/initCesium";
  import axios from "axios";
  import Chart from 'chart.js/dist/Chart.min.js';
  
  export default {
    name: "CesiumViewer",
    data() {
      return {
        viewer: null,
        handler: null,
        isGeologyPickActive: false,
        geologyData: null,
        isDrawing: false,
        isDrawingRectangle: false,
        polyline: null,
        polylinePositions: [],
        rectangleEntity: null,
        showElevationProfile: false,
        elevationChart: null,
        longitude: null,
        latitude: null,
        rectangle: {
          minLon: null,
          minLat: null,
          maxLon: null,
          maxLat: null
        },
        selectedFeatureInfo: null,
        currentDataSource: null,
        currentDataSourceRectangle: null,
        isNightMode: false,
        tempPoint: null,
        showRoadNetwork: false,
        roadNetworkLayer: null,
        useTerrainMap: false,
        terrainProvider: null,
        baseImageryLayer: null,
        searchKeyword: '',
        poiResults: [],
        isLoggedIn: false,
        showLoginModal: false,
        showUserMenu: false,
        userInfo: {
          username: '',
          email: '',
          avatar: ''
        },
        isAddPoiMode: false,
        newPoiData: {
          name: '',
          description: '',
          lonLat: '', // 合并后的经纬度输入
          imageFile: null,
          imagePreview: null
        },
        showPoiForm: false,
        loginForm: {
          userAccount: '',
          userPassword: ''
        },
        poiSearchKeyword: '', 
        poiMarkers: [],
        originalClickHandler: null,
        userPoiMarker: null,
        userPois: [],
        userPoisLoading: false,
        poiPagination: {
          currentPage: 1, // 当前页码
          pageSize: 5,   // 每页显示的条数
          total: 0,      // 总记录数
          totalPages: 1  // 总页数
        },
        showRegisterForm: false,
        registerForm: {
          userAccount: '',
          userPassword: '',
          checkPassword: '',
          phoneNumber: '',
          userName: ''
        },
        activeForm: 'login',

        isCoordinatePickActive: false, // 坐标拾取模式状态
        pickedCoordinates: null,       // 存储拾取的坐标

        captchaCountdown: 0,       // 新增倒计时状态
        isSendingCaptcha: false,   // 新增发送状态
        mailLoginForm: {
          email: '',
          verifyCode: ''
        },

        showCoordinateModal: false,
        coordinateInput: '',
        inputError: '',
         // 条件矩形查询
        showKeywordModal: false,
        geologyKeyword: '',
        currentRectangle: null,
        qduecdList: [], // 存储qduecd列表
        selectedQduecd: null, // 当前选中的qduecd
        qduecdColorMap: {
          '上三叠统': '#b195c0',
          '中三叠统': '#a177aa',
          '青白口系': '#d08251',
          '滹沱系': '#ea7c95',
          '长城系': '#f5c491',
          '蓟县系': '#f7ce9d',
          '纽芬兰统': '#95b48a',
          '第二统': '#9ebe93',
          '第三统': '#abcb9f',
          '芙蓉统': '#b5d9ad',
          '下奥陶统': '#2aac8c',
          '中奥陶统': '#45b99a',
          '上奥陶统': '#83c9ad',
          '兰多弗里统': '#addbd9',
          '文洛克统': '#b3dcd0',
          '拉德洛统': '#bee3dd',
          '普利多利统': '#e2efe4',
          '中泥盆统': '#ebc984',
          '上泥盆统': '#ecdcae',
          '上石炭统': '#9ac1c5',
          '船山统': '#e07965',
          '阳新统': '#f1a87f',
          '乐平统': '#f5c4a2',
          '下三叠统': '#986ea4',
          '下侏罗统': '#55c5f1',
          '中侏罗统': '#78cbe8',
          '上侏罗统': '#afdff7',
          '下白垩统': '#96ca78',
          '上白垩统': '#aed26a',
          '古新统': '#f2b17c',
          '始新统': '#f3ba85',
          '渐新统': '#f5c491',
          '中新统': '#fbef13',
          '上新统': '#fcf162',
          '更新统': '#fbebbb',
          '全新统': '#fef1e2',
          '古太古代': '#e8669e',
          '中太古代': '#eb86ad',
          '新太古代': '#f0abc1',
          '渐新统': '#f5c491'

        },
        activeTab: 'poi', // 'poi' 或 'track'
        trackSearchKeyword: '',
        userTracks: [],
        userTracksLoading: false,
        trackPagination: {
          currentPage: 1,
          pageSize: 5,
          total: 0,
          totalPages: 1
        },
        newTrackData: {
          name: '',
          description: '',
        },
        trackFile: null,
        trackFilePreview: null,
        isTrackUploadModalVisible: false,  // 修改数据属性名
        polygonLeftClickHandler: null, // 新增：存储多边形左键点击处理器
        polygonRightClickHandler: null, // 新增：存储多边形右键点击处理器

        // 新增区域相关数据属性
        activeTab: 'poi', // 增加 'area' 选项
        areaSearchKeyword: '', // 区域搜索关键字
        userAreas: [], // 用户区域列表
        userAreasLoading: false, // 区域加载状态
        areaPagination: { // 区域分页信息
          currentPage: 1,
          pageSize: 5,
          total: 0,
          totalPages: 1
        },
        newAreaData: { // 新区域数据
          name: '',
          description: '',
        },
        areaFile: null, // 存储上传的区域文件
        areaFilePreview: null, // 区域文件预览
        isAreaUploadModalVisible: false, // 控制区域上传模态框显示
        currentAreaEntity: null, // 当前显示的区域实体


        // 新增多边形绘制相关状态
        isDrawingPolygon: false,
        polygonPositions: [],
        tempPolygonEntity: null,
        tempPolygonPoints: [],
        tempPolygonLine: null,
        originalClickHandler: null, // 保存原始点击处理器
        polygonOriginalClickHandler: null, // 专门保存多边形模式前的处理器
        currentPolygonGeoJSON: null, // 新增：存储当前绘制的多边形GeoJSON
        currentAreaGroupEntities: [], // 存储区域组的多边形实体


        // 新增数据属性-缓冲区查询地层
        showBufferModal: false,
        bufferDistance: 1000,
        currentTrackId: null,
        bufferLoading: false, // 新增：缓冲区加载状态
        cancelTokenSource: null, // 新增：用于取消请求
        geologyDataSource: null, // 存储地质数据源
        showImageModal: false,
        currentImageUrl: '',
        currentFfid: '',
        isGeologyDetailActive: false,
        currentQueryType: null, // 'rectangle' 或 'buffer'
        currentPoiMarker: null, // 当前显示的标记点实体
        showClearControl: false, // 控制清除面板显示
        showMarkerClearButton: false, // 控制标记点清除按钮显示
        showTrackClearButton: false,  // 控制轨迹清除按钮显示
        sortBy: 'seq', // 默认按序列排序
        sortOrder: 'asc', // 排序顺序
        showStratumInfoModal: false, // 控制地层信息模态框显示
        currentStratumInfo: null,    // 当前选中的地层信息
        selectedGeologyDataSource: null, // 专门存储选择的地层数据

        currentAreaId: null,
        areaLoading: false,
        showAreaClearButton: false
      };
    },
    computed: {
      shouldShowClearControl() {
        return this.userPoiMarker || this.currentTrackEntity;
      },
      // 计算显示的区域页码
      displayedAreaPages() {
        const { currentPage, totalPages } = this.areaPagination;
        const pages = [];

        // 如果总页数小于等于2，直接显示所有页码
        if (totalPages <= 2) {
          for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          // 当前页码的前一页
          if (currentPage > 1) {
            pages.push(currentPage - 1);
          }
          // 当前页码
          pages.push(currentPage);
          // 当前页码的后一页
          if (currentPage < totalPages) {
            pages.push(currentPage + 1);
          }
        }

        return pages;
      },
      displayedPages() {
        const { currentPage, totalPages } = this.poiPagination;
        const pages = [];

        // 如果总页数小于等于2，直接显示所有页码
        if (totalPages <= 2) {
          for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          // 当前页码的前一页
          if (currentPage > 1) {
            pages.push(currentPage - 1);
          }
          // 当前页码
          pages.push(currentPage);
          // 当前页码的后一页
          if (currentPage < totalPages) {
            pages.push(currentPage + 1);
          }
        }

        return pages;
      }
    },
    mounted() {
      // 添加全局点击事件监听
      document.addEventListener('click', this.handleGlobalClick);

      document.title = "GeologyMine";
      this.viewer = initCesium(Cesium, this.$refs.cesiumContainer);

      this.viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          116.684556, // 经度
          40.553956,  // 纬度
          200000        // 视点高度（单位：米）
        ),
        orientation: {
          heading: Cesium.Math.toRadians(0),   // 正北方向
          pitch: Cesium.Math.toRadians(-90),   // 俯角30度
          roll: 0.0
        }
      });

      this.checkLoginStatus();
      
      this.viewer.scene.backgroundColor = Cesium.Color.LIGHTBLUE.withAlpha(0.3);
      this.setDayMode();

      this.viewer._cesiumWidget._creditContainer.style.display = "none";

      this.viewer.terrainProvider = new Cesium.CesiumTerrainProvider({
        url: "https://tile.siweiearth.com/terrain/",
        requestVertexNormals: true,
        requestWaterMask: true
      });

      this.initRoadNetworkLayer();
      this.initTerrainProvider();

      this.handler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);

      this.handler.setInputAction((event) => {
        const ray = this.viewer.scene.camera.getPickRay(event.position);
        const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene);

        if (cartesian) {
          const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
          const longitude = Cesium.Math.toDegrees(cartographic.longitude).toFixed(6);
          const latitude = Cesium.Math.toDegrees(cartographic.latitude).toFixed(6);

          // 新增坐标拾取逻辑
          if (this.isCoordinatePickActive) {
            this.pickedCoordinates = {
              longitude: parseFloat(longitude),
              latitude: parseFloat(latitude),
              height: cartographic.height.toFixed(2)
            }
            return // 阻止其他处理逻辑
          }

          this.longitude = parseFloat(longitude);
          this.latitude = parseFloat(latitude);

          if (this.isGeologyPickActive) {
            this.callGeologyApi(this.longitude, this.latitude);
          }

          if (this.isDrawing) {
            this.polylinePositions.push(cartesian);
            this.updatePolyline();
          }

          if (this.isDrawingRectangle) {
            if (!this.rectangle.minLon) {
              this.rectangle.minLon = this.longitude;
              this.rectangle.minLat = this.latitude;
              
              if (this.tempPoint) {
                this.viewer.entities.remove(this.tempPoint);
              }
              this.tempPoint = this.viewer.entities.add({
                position: cartesian,
                point: {
                  pixelSize: 2,
                  color: Cesium.Color.WHITE
                }
              });
            } else {
              this.rectangle.maxLon = this.longitude;
              this.rectangle.maxLat = this.latitude;
              
              this.updateRectangleOnMap(); // 更新矩形
              this.drawRectangleOnMap();

              this.viewer.entities.remove(this.rectangleEntity);
              this.isDrawingRectangle = false;
            }
          }
    
          const pickedObject = this.viewer.scene.pick(event.position);
          if (Cesium.defined(pickedObject)) {
              // 检查是否有ffid属性
              if (pickedObject.id && pickedObject.id.properties && pickedObject.id.properties.ffid) {
                  const ffid = pickedObject.id.properties.ffid.getValue();
                  if (ffid) {
                      this.showGeologyImage(ffid);
                  }
              }
          }

          if (Cesium.defined(pickedObject) && pickedObject.id && pickedObject.id.properties) {
            const properties = pickedObject.id.properties;
            if (properties.qduecd) {
              this.selectedFeatureInfo = properties.qduecd.getValue();
            }
          }
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      // 鼠标移动事件监听器
      this.handler.setInputAction((movement) => {
        if (this.isDrawingRectangle && this.rectangle.minLon !== null) {
          const ray = this.viewer.scene.camera.getPickRay(movement.endPosition);
          const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene);

          if (cartesian) {
            const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
            const longitude = Cesium.Math.toDegrees(cartographic.longitude).toFixed(6);
            const latitude = Cesium.Math.toDegrees(cartographic.latitude).toFixed(6);

            this.rectangle.maxLon = parseFloat(longitude);
            this.rectangle.maxLat = parseFloat(latitude);

            this.updateRectangleOnMap(); // 更新矩形
          }
        }
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    },

    watch: {
      // 监听标记点变化
      userPoiMarker(newVal) {
        this.updateClearControlVisibility();
      },
      // 监听轨迹变化
      currentTrackEntity(newVal) {

        this.updateClearControlVisibility();
      },
      // 监听清除条件变化
      shouldShowClearControl(newVal) {
        this.showClearControl = newVal;
      },
      showCoordinateModal(newVal) {
        if (newVal) {
          this.openCoordinateInput()
        } else {
          this.coordinateInput = ''
          this.inputError = ''
        }
      }
    },

    methods: {
      // 加载区域组（多个多边形）
      async loadAreaGroup(area) {
        try {
          // 清除之前的区域
          this.clearCurrentArea();
          
          const token = localStorage.getItem('userToken');
          if (!token) {
            alert('请先登录');
            this.showLoginModal = true;
            return;
          }
          
          // 调用接口获取区域组的多边形
          const response = await axios.get(
            `https://geologymine.fun/geotools/getPolygonsByGroupId?groupId=${area.groupid}`,
            {
              headers: {
                'Token': token
              }
            }
          );
          
          if (response.data.errcode === 0) {
            const polygons = response.data.body;
            const entities = [];
            
            polygons.forEach(polygonData => {
              const geom = JSON.parse(polygonData.geom);
              if (geom.type === 'MultiPolygon') {
                // 对于MultiPolygon，每个坐标数组都是一个多边形环
                geom.coordinates.forEach(polygonCoords => {
                  const positions = polygonCoords[0].map(coord => 
                    Cesium.Cartesian3.fromDegrees(coord[0], coord[1])
                  );
                  const entity = this.viewer.entities.add({
                    name: polygonData.polygonName || '区域组多边形',
                    polygon: {
                      hierarchy: new Cesium.PolygonHierarchy(positions),
                      material: Cesium.Color.BLUE.withAlpha(0.3),
                      outline: true,
                      outlineColor: Cesium.Color.BLUE
                    }
                  });
                  entities.push(entity);
                });
              } else if (geom.type === 'Polygon') {
                // 处理单个多边形
                const positions = geom.coordinates[0].map(coord => 
                  Cesium.Cartesian3.fromDegrees(coord[0], coord[1])
                );
                const entity = this.viewer.entities.add({
                  name: polygonData.polygonName || '区域组多边形',
                  polygon: {
                    hierarchy: new Cesium.PolygonHierarchy(positions),
                    material: Cesium.Color.BLUE.withAlpha(0.3),
                    outline: true,
                    outlineColor: Cesium.Color.BLUE
                  }
                });
                entities.push(entity);
              }
            });
            
            // 存储当前实体数组
            this.currentAreaGroupEntities = entities;
            
            // 显示清除按钮
            this.showAreaClearButton = true;
            
            // 如果有实体，飞向第一个实体
            if (entities.length > 0) {
              this.viewer.flyTo(entities[0]);
            }
          } else {
            alert(response.data.msg || '获取区域数据失败');
          }
        } catch (e) {
          console.error('加载区域组失败:', e);
          alert('区域组加载错误: ' + e.message);
        }
      },


      // 切换区域页码
      changeAreaPage(page) {
        if (page < 1 || page > this.areaPagination.totalPages || page === this.areaPagination.currentPage) {
          return;
        }
        
        this.areaPagination.currentPage = page;
        this.fetchUserAreas();
        
        // 滚动到列表顶部
        const container = this.$el.querySelector('.area-list');
        if (container) container.scrollTop = 0;
      },
      
      // 搜索用户区域
      searchUserAreas() {
        this.areaPagination.currentPage = 1;
        this.fetchUserAreas();
      },
      
      // 清除区域搜索
      clearAreaSearch() {
        this.areaSearchKeyword = '';
        this.searchUserAreas();
      },
      
      // 获取用户区域列表
      async fetchUserAreas() {
        this.userAreasLoading = true;
        try {
          const token = localStorage.getItem('userToken');
          const params = {
            currentPage: this.areaPagination.currentPage,
            pageSize: this.areaPagination.pageSize,
            areaName: this.areaSearchKeyword
          };
          
          const response = await axios.get('https://geologymine.fun/geotools/getPolygons', {
            headers: {
              'Token': token
            },
            params: params
          });
          
          if (response.data.errcode === 0) {
            this.userAreas = response.data.body.data || [];
            this.areaPagination.total = response.data.body.total || 0;
            this.areaPagination.totalPages = Math.ceil(this.areaPagination.total / this.areaPagination.pageSize);
          } else {
            alert(response.data.msg || '获取区域列表失败');
          }
        } catch (error) {
          console.error('获取区域列表失败:', error);
          alert('获取区域列表失败，请检查网络连接');
        } finally {
          this.userAreasLoading = false;
        }
      },

      async flyToArea(area) {
          try {
              // 清除之前的区域
              this.clearCurrentArea();
              // this.currentAreaEntity = null;  // 重置引用
              
              // 解析区域几何数据
              const geom = JSON.parse(area.geom);
              if (!geom || !geom.coordinates) {
                  alert('无效的区域数据');
                  return;
              }
              
              // 根据几何类型创建实体
              let entity;
              switch(geom.type) {
                  case 'Polygon':
                      // ... 原有多边形处理代码 ...
                      break;
                      
                  case 'MultiPolygon':
                      // 创建MultiPolygon实体（多个多边形）
                      const entities = [];
                      geom.coordinates.forEach(polygonCoords => {
                          const positions = polygonCoords[0].map(coord => 
                              Cesium.Cartesian3.fromDegrees(coord[0], coord[1])
                          );
                          entities.push(this.viewer.entities.add({
                              name: area.name || '用户区域',
                              polygon: {
                                  hierarchy: new Cesium.PolygonHierarchy(positions),
                                  material: Cesium.Color.BLUE.withAlpha(0.3),
                                  outline: true,
                                  outlineColor: Cesium.Color.BLUE
                              }
                          }));
                      });
                      entity = entities; // 存储实体数组
                      break;
                      
                  case 'Point':
                      // ... 原有点处理代码 ...
                      break;
                      
                  case 'LineString':
                      // ... 原有线处理代码 ...
                      break;
                      
                  default:
                      alert('不支持的区域类型: ' + geom.type);
                      return;
              }

              // 存储当前实体（单个实体或实体数组）
              this.currentAreaEntity = entity;

              // 显示轨迹清除按钮
              this.showAreaClearButton = true;

              // 飞到实体（如果是实体数组则飞向第一个实体）
              const targetEntity = Array.isArray(entity) ? entity[0] : entity;
              this.viewer.flyTo(targetEntity);
          } catch (e) {
              console.error('加载区域失败:', e);
              alert('区域加载错误: ' + e.message);
          }
      },
      
      // 删除区域
      async deleteArea(area) {
        if (!confirm(`确定要删除区域【${area.polygonName}】吗？`)) return;
        
        try {
          const token = localStorage.getItem('userToken');
          const response = await axios.get(
            `https://geologymine.fun/geotools/deletePolygon?polygonId=${area.groupid}`,
            {
              headers: {
                'Token': token
              }
            }
          );
          
          if (response.data.errcode === 0) {
            // 从当前列表中移除
            this.userAreas = this.userAreas.filter(a => a.id !== area.id);
            this.areaPagination.total -= 1;
            
            // 如果删除的是当前页最后一项且不是第一页
            if (this.userAreas.length === 0 && this.areaPagination.currentPage > 1) {
              this.areaPagination.currentPage -= 1;
              this.fetchUserAreas();
            }
            
            // 如果删除的是当前显示的区域，清除实体
            if (this.currentAreaEntity && this.currentAreaEntity.id === area.id) {
              this.clearCurrentArea();
            }
          } else {
            alert(response.data.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除区域失败:', error);
          alert('删除失败，请检查网络连接');
        }
      },
      
      // 清除当前区域的实体（支持单个区域和区域组）
      clearCurrentArea() {
        // 清除单个区域实体
        if (this.currentAreaEntity) {
          if (Array.isArray(this.currentAreaEntity)) {
            this.currentAreaEntity.forEach(entity => {
              this.viewer.entities.remove(entity);
            });
          } else {
            this.viewer.entities.remove(this.currentAreaEntity);
          }
          this.currentAreaEntity = null;
        }
        
        // 清除区域组实体
        if (this.currentAreaGroupEntities.length > 0) {
          this.currentAreaGroupEntities.forEach(entity => {
            this.viewer.entities.remove(entity);
          });
          this.currentAreaGroupEntities = [];
        }
        
        this.showAreaClearButton = false; // 隐藏清除按钮
      },
      
      // 显示区域上传模态框
      showAreaUploadModal(event) {
        event?.preventDefault();
        event?.stopPropagation();
        
        // 检查是否登录
        if (!this.isLoggedIn) {
          this.showLoginModal = true;
          return;
        }
        
        // 重置表单数据
        this.newAreaData = { name: '', description: '' };
        this.areaFile = null;
        this.areaFilePreview = null;
        
        // 显示模态框
        this.isAreaUploadModalVisible = true;
      },
      
      // 关闭区域上传模态框
      closeAreaUploadModal() {
        this.isAreaUploadModalVisible = false;
      },
      
      // 处理区域文件选择
      handleAreaFileChange(event) {
        const file = event.target.files[0];
        if (file) {

          
          this.areaFile = file;
          this.areaFilePreview = {
            name: file.name,
            size: file.size
          };
        }
      },
      
      async uploadArea() {
        if (!this.areaFile) {
          alert('请选择区域文件');
          return;
        }

        if (!this.newAreaData.name.trim()) {
          alert('请输入区域名称');
          return;
        }

        try {
          const formData = new FormData();
          formData.append('file', this.areaFile); // 文件字段名保持为 "file"
          formData.append('name', this.newAreaData.name);
          formData.append('description', this.newAreaData.description || '');

          const token = localStorage.getItem('userToken');
          const response = await axios.post(
            'https://geologymine.fun/geotools/savePolygon', // 修改为新的接口地址
            formData,
            {
              headers: {
                'Token': token,
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.errcode === 0) {
            alert('区域上传成功');
            this.closeAreaUploadModal();
            this.fetchUserAreas(); // 刷新区域列表
          } else {
            alert(response.data.msg || '区域上传失败');
          }
        } catch (error) {
          console.error('区域上传失败:', error);
          alert('区域上传失败，请检查网络连接');
        }
      },


      // 清除地质数据
      clearGeologyData() {
        this.qduecdList = [];
        this.currentStratumInfo = null;
        
        if (this.currentDataSource) {
          this.viewer.dataSources.remove(this.currentDataSource);
          this.currentDataSource = null;
        }
        if (this.currentDataSourceRectangle) {
          this.viewer.dataSources.remove(this.currentDataSourceRectangle);
          this.currentDataSourceRectangle = null;
        }
        if (this.selectedGeologyDataSource) {
          this.viewer.dataSources.remove(this.selectedGeologyDataSource);
          this.selectedGeologyDataSource = null;
        }
      },
      
      // 清除多边形绘制
      clearPolygonDrawing() {
        if (this.tempPolygonEntity) {
          this.viewer.entities.remove(this.tempPolygonEntity);
          this.tempPolygonEntity = null;
        }
        
        if (this.tempPolygonLine) {
          this.viewer.entities.remove(this.tempPolygonLine);
          this.tempPolygonLine = null;
        }
        
        this.tempPolygonPoints.forEach(point => {
          this.viewer.entities.remove(point);
        });
        this.tempPolygonPoints = [];
      },
      
      // 清除矩形绘制 
      clearRectangleDrawing() {
        if (this.tempPoint) {
          this.viewer.entities.remove(this.tempPoint);
          this.tempPoint = null;
        }
        
        if (this.rectangleEntity) {
          this.viewer.entities.remove(this.rectangleEntity);
          this.rectangleEntity = null;
        }
      },


      // 切换多边形绘制模式
      toggleDrawingPolygon() {
        // 如果当前已经是多边形模式，则退出
        if (this.isDrawingPolygon) {
          this.exitPolygonDrawing();

          this.isDrawingPolygon = false;
          return;
        }
        
        // 进入多边形模式
        this.isDrawingPolygon = true;
        
        // 1. 关闭矩形模式
        this.isDrawingRectangle = false;
        this.clearRectangleDrawing();
        
        // 2. 清除地质数据
        this.clearGeologyData();
        
        // 3. 清除多边形临时实体
        this.polygonPositions = [];
        this.clearPolygonDrawing();
        
        // 4. 保存原始点击处理器
        if (!this.originalClickHandler) {
          this.originalClickHandler = this.handler.getInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
        }
        
        // 5. 设置多边形专用事件处理器
        this.setupPolygonEventHandlers();
        
      },

      // 设置多边形事件处理器
      setupPolygonEventHandlers() {
        // 移除可能存在的旧处理器
        this.removePolygonEventHandlers();
        
        // 设置鼠标移动处理器
        this.polygonMouseMoveHandler = (movement) => {
          if (this.polygonPositions.length > 0) {
            const ray = this.viewer.scene.camera.getPickRay(movement.endPosition);
            const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene);
            
            if (cartesian && this.tempPolygonLine) {
              const positions = [...this.polygonPositions, cartesian];
              this.tempPolygonLine.polyline.positions = new Cesium.CallbackProperty(() => positions, false);
            }
          }
        };
        
        // 设置左键点击处理器
        this.polygonLeftClickHandler = (event) => {
          const ray = this.viewer.scene.camera.getPickRay(event.position);
          const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene);
          
          if (cartesian) {
            this.addPolygonPoint(cartesian);
          }
        };
        
        // 设置右键点击处理器
        this.polygonRightClickHandler = (event) => {
          if (this.polygonPositions.length >= 3) {
            this.finishPolygonDrawing();
          }
        };
        
        // 设置ESC键取消处理器
        this.escKeyHandler = (event) => {
          if (event.keyCode === 27) { // ESC键
            this.cancelPolygonDrawing();
          }
        };
        
        // 注册事件处理器
        this.handler.setInputAction(
          this.polygonMouseMoveHandler,
          Cesium.ScreenSpaceEventType.MOUSE_MOVE
        );
        
        this.handler.setInputAction(
          this.polygonLeftClickHandler,
          Cesium.ScreenSpaceEventType.LEFT_CLICK
        );
        
        this.handler.setInputAction(
          this.polygonRightClickHandler,
          Cesium.ScreenSpaceEventType.RIGHT_CLICK
        );
        
        // 添加键盘事件监听
        document.addEventListener('keydown', this.escKeyHandler);
        
        // 设置多边形专用点击处理器
        this.handler.setInputAction(
          this.polygonLeftClickHandler,
          Cesium.ScreenSpaceEventType.LEFT_CLICK
        );
      },


      // 新增：退出多边形绘制模式
      exitPolygonDrawing() {
        // 1. 移除所有多边形专用事件处理器
        this.removePolygonEventHandlers();
        
        // 2. 清除多边形临时实体
        this.polygonPositions = [];
        this.clearPolygonDrawing();
        
        // 3. 恢复原始点击处理器
        if (this.originalClickHandler) {
          this.handler.setInputAction(
            this.originalClickHandler,
            Cesium.ScreenSpaceEventType.LEFT_CLICK
          );
        }
      },

      // 新增：移除多边形事件处理器
      removePolygonEventHandlers() {
        // 移除鼠标移动处理器
        this.handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        
        // 移除左键点击处理器
        if (this.polygonLeftClickHandler) {
          this.handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
          this.polygonLeftClickHandler = null;
        }
        
        // 移除右键点击处理器
        if (this.polygonRightClickHandler) {
          this.handler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_CLICK);
          this.polygonRightClickHandler = null;
        }
      },

      // 开始多边形绘制（点击处理）
      startPolygonDrawing() {
        // 保存当前左键点击处理器
        this.polygonLeftClickHandler = (event) => {
          const ray = this.viewer.scene.camera.getPickRay(event.position);
          const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene);
          
          if (cartesian) {
            this.addPolygonPoint(cartesian);
          }
        };
        
        // 保存当前右键点击处理器
        this.polygonRightClickHandler = (event) => {
          if (this.polygonPositions.length >= 3) {
            this.finishPolygonDrawing();
          }
        };
        
        // 设置新的点击处理器
        this.handler.setInputAction(
          this.polygonLeftClickHandler,
          Cesium.ScreenSpaceEventType.LEFT_CLICK
        );
        
        this.handler.setInputAction(
          this.polygonRightClickHandler,
          Cesium.ScreenSpaceEventType.RIGHT_CLICK
        );
      },
      
      // 新增：添加多边形点
      addPolygonPoint(cartesian) {
        this.polygonPositions.push(cartesian);
        
        // 添加点实体
        const point = this.viewer.entities.add({
          position: cartesian,
          point: {
            pixelSize: 8,
            // color: Cesium.Color.YELLOW,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 2
          }
        });
        this.tempPolygonPoints.push(point);
        
        // 如果是第一个点，创建临时线
        if (this.polygonPositions.length === 1) {
          this.tempPolygonLine = this.viewer.entities.add({
            polyline: {
              positions: new Cesium.CallbackProperty(() => 
                [...this.polygonPositions, this.polygonPositions[0]], 
                false
              ),
              width: 2,
              material: Cesium.Color.CYAN.withAlpha(0.7)
            }
          });
        }
        
        // 如果有三个或更多点，创建临时多边形
        if (this.polygonPositions.length >= 3) {
          if (this.tempPolygonEntity) {
            this.viewer.entities.remove(this.tempPolygonEntity);
          }
          
          this.tempPolygonEntity = this.viewer.entities.add({
            polygon: {
              hierarchy: new Cesium.CallbackProperty(() => 
                new Cesium.PolygonHierarchy(this.polygonPositions), 
                false
              ),
              material: Cesium.Color.CYAN.withAlpha(0.2),
              outline: true,
              outlineColor: Cesium.Color.CYAN
            }
          });
        }
      },
      
      // 新增：完成多边形绘制
      async finishPolygonDrawing() {
          if (this.polygonPositions.length < 3) {
          this.$notify({
            title: '绘制失败',
            message: '多边形至少需要3个点',
            type: 'warning',
            duration: 2000
          });
          return;
        }


        if (this.polygonPositions.length < 3) {
          alert('多边形至少需要3个点');
          return;
        }
        
        // 转换坐标
        const positions = this.polygonPositions.map(cartesian => {
          const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
          return [
            Cesium.Math.toDegrees(cartographic.longitude),
            Cesium.Math.toDegrees(cartographic.latitude)
          ];
        });
        
        // 确保多边形闭合
        positions.push(positions[0]);
        
        // 创建GeoJSON多边形
        const polygonGeoJSON = {
          type: "Polygon",
          coordinates: [positions]
        };
        

        // 存储多边形GeoJSON以供后续使用
        this.currentPolygonGeoJSON = JSON.stringify(polygonGeoJSON);

        // 设置查询类型
        this.currentQueryType = 'polygon';
        
        // 调用API查询地质信息
        try {
          const token = localStorage.getItem('userToken');
          if (!token) {
            alert('请登录后进行地质查询');
            this.showLoginModal = true;
            return;
          }
          
          const response = await axios.post(
            'https://geologymine.fun/geotools/getGeologyFileByPolygon',
            {
              polygonGeoJSON: JSON.stringify(polygonGeoJSON)
            },
            {
              headers: {
                'Token': token
              }
            }
          );
          
          if (response.data.errcode === 0) {
            // 处理返回的地质数据
            const geojson = JSON.parse(response.data.body.geojsonFeaturecollection);
            
            // 使用复合键(qduecd+tong)去重
            const strataMap = new Map();
            geojson.features.forEach(feature => {
              if (feature.properties && feature.properties.qduecd && feature.properties.tong) {
                const qduecd = feature.properties.qduecd;
                const tong = feature.properties.tong;
                const seq = parseInt(feature.properties.seq) || 0;
                const lower_age = feature.properties.lower_age;
                const upper_age = feature.properties.upper_age;
                
                const compositeKey = `${qduecd}_${tong}`;
                
                if (!strataMap.has(compositeKey) || seq < strataMap.get(compositeKey).seq) {
                  strataMap.set(compositeKey, {
                    qduecd: qduecd,
                    tong: tong,
                    seq: seq,
                    lower_age: lower_age,
                    upper_age: upper_age,
                    compositeKey: compositeKey
                  });
                }
              }
            });
            
            this.qduecdList = Array.from(strataMap.values())
              .sort((a, b) => a.seq - b.seq);
            
            // 添加地质数据到地图
            this.addGeoJsonToCesium(response.data.body.geojsonFeaturecollection);
            
            // 清除临时绘制
            this.clearPolygonDrawing();
            this.isDrawingPolygon = false;
            // 退出多边形模式
            this.exitPolygonDrawing();

          } else {
            alert(response.data.msg || '地质数据查询失败');
          }
        } catch (error) {
          console.error('地质信息查询失败:', error);
          alert('查询失败，请检查网络连接');
        }
      },
      
      // 清除多边形绘制
      clearPolygonDrawing() {
        if (this.tempPolygonEntity) {
          this.viewer.entities.remove(this.tempPolygonEntity);
          this.tempPolygonEntity = null;
        }
        
        if (this.tempPolygonLine) {
          this.viewer.entities.remove(this.tempPolygonLine);
          this.tempPolygonLine = null;
        }
        
        this.tempPolygonPoints.forEach(point => {
          this.viewer.entities.remove(point);
        });
        this.tempPolygonPoints = [];
      },

      // 在methods中添加
      clearStratumInfo() {
        this.currentStratumInfo = null;
      },

      // 排序地层列表
      sortQduecdList(criteria) {
        if (this.sortBy === criteria) {
          // 切换排序顺序
          this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
          // 设置新的排序标准
          this.sortBy = criteria;
          this.sortOrder = 'asc';
        }
        
        // 执行排序
        this.qduecdList.sort((a, b) => {
          let valueA, valueB;
          
          if (criteria === 'seq') {
            // 按序列排序
            valueA = a.seq || 0;
            valueB = b.seq || 0;
          } else {
            // 按名称排序
            valueA = a.name || '';
            valueB = b.name || '';
          }
          
          // 排序比较
          let comparison = 0;
          if (valueA > valueB) comparison = 1;
          if (valueA < valueB) comparison = -1;
          
          // 应用排序顺序
          return this.sortOrder === 'asc' ? comparison : -comparison;
        });
      },


      // 更新清除面板可见性
      updateClearControlVisibility() {
        this.showClearControl = this.userPoiMarker || this.currentTrackEntity;
      },
      // 清除当前标记点
      clearCurrentMarker() {
        if (this.userPoiMarker) {
          this.viewer.entities.remove(this.userPoiMarker);
          this.userPoiMarker = null;
        }
        this.showMarkerClearButton = false; // 隐藏清除按钮
      },
      
      // 清除当前轨迹
      clearCurrentTrack() {
        if (this.currentTrackEntity) {
          this.viewer.entities.remove(this.currentTrackEntity);
          this.currentTrackEntity = null;
          this.currentTrackId = null;
        }
        this.showTrackClearButton = false; // 隐藏清除按钮
      },

      // 修改为切换地质详情的方法
      toggleGeologyDetail() {
        this.isGeologyDetailActive = !this.isGeologyDetailActive;
        if (this.isGeologyDetailActive) {
          this.loadGeologyDetail();
        } else {
          // 关闭时移除地质数据源
          if (this.geologyDataSource) {
            this.viewer.dataSources.remove(this.geologyDataSource);
            this.geologyDataSource = null;
          }
        }
      },

      async showGeologyImage(ffid) {
        try {
          const token = localStorage.getItem('userToken');
          if (!token) {
            alert('请登录后查看地质图');
            this.showLoginModal = true;
            return;
          }

          // 构造带Token的图片URL
          const imageUrl = `https://geologymine.fun/geotools/getGeologyImage?fileName=${ffid}.jpg&token=${token}`;
          
          // 创建一个新窗口或模态框显示图片
          window.open(imageUrl, '_blank', 'width=800,height=600');
        } catch (error) {
          console.error('获取地质图片失败:', error);
          alert('获取地质图片失败，请检查登录状态');
        }
      },

        closeImageModal() {
          this.showImageModal = false;
          this.currentImageUrl = '';
          this.currentFfid = '';
      },
      // // 显示地质图片
      // showGeologyImage(ffid) {
      //     // 构造图片URL
      //     const imageUrl = `https://geologymine.fun/geotools/getGeologyImage?fileName=${ffid}.jpg`;
          
      //     // 创建一个新窗口或模态框显示图片
      //     window.open(imageUrl, '_blank', 'width=800,height=600');
          
      //     // 或者使用更优雅的方式，如创建一个图片查看器模态框
      //     // this.$emit('show-geology-image', imageUrl);
      // },


      // 新增：加载地质详情
      async loadGeologyDetail() {
        try {
          // 清除旧地质数据
          if (this.geologyDataSource) {
            this.viewer.dataSources.remove(this.geologyDataSource);
            this.geologyDataSource = null;
          }
          
          const response = await axios.get('https://geologymine.fun/geotools/getGeojson');
          
          if (response.data.errcode === 0) {
            // 解析geojson字符串（因为返回的body是字符串）
            const geojson = JSON.parse(response.data.body);
            
            // 加载geojson
            const dataSource = await Cesium.GeoJsonDataSource.load(geojson, {
              stroke: Cesium.Color.WHITE,
              fill: Cesium.Color.GREEN.withAlpha(0.1),
              strokeWidth: 2
            });
            
            this.viewer.dataSources.add(dataSource);
            this.geologyDataSource = dataSource;
            
            // 自动缩放到数据范围
            this.viewer.flyTo(dataSource, {
              orientation: {
                heading: Cesium.Math.toRadians(0),
                pitch: Cesium.Math.toRadians(-90),
                roll: 30.0
              }
            });
            
            // 提取所有ffid并显示在控制台
            const ffids = new Set();
            dataSource.entities.values.forEach(entity => {
              if (entity.properties && entity.properties.ffid) {
                ffids.add(entity.properties.ffid.getValue());
              }
            });
            console.log('地质图块FFID列表:', Array.from(ffids));
            
            // 可以在这里添加更多处理逻辑，比如显示FFID列表
          } else {
            alert(response.data.msg);
          }
        } catch (error) {
          console.error('加载地质详情失败:', error);
          alert('加载失败');
        }
      },
      // 显示轨迹的缓冲区查询模态框
      async showBufferForTrack(track) {
        try {
          // 清除旧轨迹
          this.clearCurrentTrack();
          
          // 获取用户token
          const token = localStorage.getItem('userToken');
          if (!token) {
            alert('请先登录');
            this.showLoginModal = true;
            return;
          }

          // 调用接口获取轨迹几何数据
          const response = await axios.get(
            `https://geologymine.fun/track/getTrackGeomById?trackId=${track.id}`,
            {
              headers: {
                'Token': token
              }
            }
          );

          if (response.data.errcode === 0 && response.data.body) {
            const trackData = response.data.body;
            
            // 解析轨迹数据
            const geom = JSON.parse(trackData.geom);
            if (geom.type !== 'LineString' || !geom.coordinates || geom.coordinates.length < 2) {
              alert('无效的轨迹数据');
              return;
            }

            // 在地图上显示轨迹（传递两个参数）
            this.displayTrackOnMap(track, geom);
            
            // 显示缓冲区查询模态框
            this.showBufferModal = true;
            this.currentTrackId = track.id;
          } else {
            alert(response.data.msg || '获取轨迹数据失败');
          }
        } catch (e) {
          console.error('轨迹获取或解析失败', e);
          alert('轨迹获取或解析失败');
        }
      },


      // 格式化时间（只保留日期和时分秒）
      formatTime(timeStr) {
        if (!timeStr) return '';
        // 去掉T和时区信息
        return timeStr.replace('T', ' ').replace(/\.\d+Z?$/, '');
      },
      // 切换轨迹页码
      changeTrackPage(page) {
        if (page < 1 || page > this.trackPagination.totalPages || page === this.trackPagination.currentPage) {
          return;
        }
        
        this.trackPagination.currentPage = page;
        this.fetchUserTracks();
        
        // 滚动到列表顶部
        const container = this.$el.querySelector('.track-list');
        if (container) container.scrollTop = 0;
      },

      // 计算显示的轨迹页码
      displayedTrackPages() {
        const { currentPage, totalPages } = this.trackPagination;
        const pages = [];

        // 如果总页数小于等于2，直接显示所有页码
        if (totalPages <= 2) {
          for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          // 当前页码的前一页
          if (currentPage > 1) {
            pages.push(currentPage - 1);
          }
          // 当前页码
          pages.push(currentPage);
          // 当前页码的后一页
          if (currentPage < totalPages) {
            pages.push(currentPage + 1);
          }
        }

        return pages;
      },


      showTrackUploadModal(event) {
        event?.preventDefault(); // 防止默认行为
        event?.stopPropagation(); // 阻止事件冒泡
        
        // 检查是否登录
        if (!this.isLoggedIn) {
          this.showLoginModal = true;
          return;
        }
        
        // 重置表单数据
        this.newTrackData = { name: '', description: '' };
        this.trackFile = null;
        this.trackFilePreview = null;
        
        // 显示模态框
        this.isTrackUploadModalVisible = true;
      },

      closeTrackUploadModal() {
        this.isTrackUploadModalVisible = false;
      },
      
      // 处理轨迹文件选择
      handleTrackFileChange(event) {
        const file = event.target.files[0];
        if (file) {
          // 检查文件类型
          const validTypes = ['application/gpx+xml', 'application/vnd.google-earth.kml+xml'];
          if (!validTypes.includes(file.type)) {
            alert('请选择GPX或KML格式的轨迹文件');
            this.$refs.trackFileInput.value = '';
            return;
          }
          
          this.trackFile = file;
          this.trackFilePreview = {
            name: file.name,
            size: file.size
          };
        }
      },

      // 格式化文件大小
      formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      },
      
      // 上传轨迹
      async uploadTrack() {
        if (!this.trackFile) {
          alert('请选择轨迹文件');
          return;
        }
        
        if (!this.newTrackData.name.trim()) {
          alert('请输入轨迹名称');
          return;
        }
        
        try {
          const formData = new FormData();
          formData.append('file', this.trackFile);
          formData.append('name', this.newTrackData.name);
          formData.append('description', this.newTrackData.description || '');
          
          const token = localStorage.getItem('userToken');
          const response = await axios.post('https://geologymine.fun/track/uploadTrack', formData, {
            headers: {
              'Token': token,
              'Content-Type': 'multipart/form-data'
            },
            // params: {
            //   name: this.newTrackData.name,
            //   description: this.newTrackData.description
            // }
          });
          
          if (response.data.errcode === 0) {
            alert('轨迹上传成功');
            this.closeTrackUploadModal();
            this.fetchUserTracks(); // 刷新轨迹列表
          } else {
            alert(response.data.msg || '轨迹上传失败');
          }
        } catch (error) {
          console.error('轨迹上传失败:', error);
          alert('轨迹上传失败，请检查网络连接');
        }
      },

      // 获取用户轨迹列表
        // 获取用户轨迹列表(直接获取距离)
      async fetchUserTracks() {
        this.userTracksLoading = true;
        try {
          const token = localStorage.getItem('userToken');
          
          // 使用新接口 getTracksByName
          const apiUrl = 'https://geologymine.fun/track/getTracksByName';
          const params = {
            currentPage: this.trackPagination.currentPage,
            pageSize: this.trackPagination.pageSize,
            trackName: this.trackSearchKeyword // 使用 trackName 参数
          };

          const response = await axios.get(apiUrl, {
            headers: {
              'Token': token
            },
            params: params
          });
          
          if (response.data.errcode === 0) {
            this.userTracks = response.data.body.data.map(track => {
              return {
                ...track,
              };
            });
            
            this.trackPagination.total = response.data.body.total || 0;
            this.trackPagination.totalPages = Math.ceil(this.trackPagination.total / this.trackPagination.pageSize);
          }
        } catch (error) {
          console.error('获取轨迹列表失败:', error);
        } finally {
          this.userTracksLoading = false;
        }
      },
      // async fetchUserTracks() {
      //   this.userTracksLoading = true;
      //   try {
      //     const token = localStorage.getItem('userToken');
      //     const response = await axios.get('https://geologymine.fun/track/getTracks', {
      //       headers: {
      //         'Token': token
      //       },
      //       params: {
      //         currentPage: this.trackPagination.currentPage,
      //         pageSize: this.trackPagination.pageSize,
      //         name: this.trackSearchKeyword
      //       }
      //     });
          
      //     if (response.data.errcode === 0) {
      //       this.userTracks = response.data.body.data.map(track => {
      //         // 计算轨迹距离
      //         let distance = 0;
      //         try {
      //           const geom = JSON.parse(track.geom);
      //           if (geom.type === 'LineString' && geom.coordinates && geom.coordinates.length > 1) {
      //             for (let i = 1; i < geom.coordinates.length; i++) {
      //               const [lon1, lat1] = geom.coordinates[i-1];
      //               const [lon2, lat2] = geom.coordinates[i];
                    
      //               // 使用Haversine公式计算距离
      //               const R = 6371; // 地球半径(km)
      //               const dLat = (lat2 - lat1) * Math.PI / 180;
      //               const dLon = (lon2 - lon1) * Math.PI / 180;
      //               const a = 
      //                 Math.sin(dLat/2) * Math.sin(dLat/2) +
      //                 Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      //                 Math.sin(dLon/2) * Math.sin(dLon/2);
      //               const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
      //               distance += R * c;
      //             }
      //           }
      //         } catch (e) {
      //           console.error('距离计算失败', e);
      //         }
              
      //         return {
      //           ...track,
      //           distance: distance.toFixed(2) // 保留两位小数
      //         };
      //       });
            
      //       this.trackPagination.total = response.data.body.total || 0;
      //       this.trackPagination.totalPages = Math.ceil(this.trackPagination.total / this.trackPagination.pageSize);
      //     }
      //   } catch (error) {
      //     console.error('获取轨迹列表失败:', error);
      //   } finally {
      //     this.userTracksLoading = false;
      //   }
      // },

      // 搜索用户轨迹
      searchUserTracks() {
        this.trackPagination.currentPage = 1;
        this.fetchUserTracks();
      },


      // 关闭轨迹缓冲区模态框
      closeBufferModal() {
        this.showBufferModal = false;
        // this.currentTrackId = null;
      },

      async loadGeologyByTrackBuffer() {
        if (!this.currentTrackId || !this.bufferDistance) {
          alert('请填写缓冲区距离');
          return;
        }

        // 创建取消令牌源
        this.cancelTokenSource = axios.CancelToken.source();
        
        // 显示加载状态
        this.bufferLoading = true;
        // 设置查询类型
        this.currentQueryType = 'buffer';
        
        try {
          // 清除旧地质数据
          if (this.currentGeologyDataSource) {
            this.viewer.dataSources.remove(this.currentGeologyDataSource);
          }
          
          // 调用接口
          const response = await axios.get(
            'https://geologymine.fun/track/getGeologyFileByTrackBuffer',
            {
              params: {
                trackId: this.currentTrackId,
                buffer: this.bufferDistance
              },
              cancelToken: this.cancelTokenSource.token // 添加取消令牌
            }
          );
          
          if (response.data.errcode === 0) {
            // 添加地质数据到地图
            const dataSource = this.addGeoJsonToCesium(response.data.body.geojsonFeaturecollection);
            
            // 提取所有地层代码(qduecd)并去重
            const qduecdSet = new Set();
            const geojson = JSON.parse(response.data.body.geojsonFeaturecollection);
            // 修改地层信息提取方式 - 使用复合键(qduecd+tong)去重
            const strataMap = new Map();
            geojson.features.forEach(feature => {
              if (feature.properties && feature.properties.qduecd && feature.properties.tong) {
                const qduecd = feature.properties.qduecd;
                const tong = feature.properties.tong;
                const seq = parseInt(feature.properties.seq) || 0;
                const lower_age = feature.properties.lower_age;
                const upper_age = feature.properties.upper_age;
                
                // 创建复合键：qduecd + tong
                const compositeKey = `${qduecd}_${tong}`;
                
                // 如果地层代码不存在或当前特征的seq更小（更老），则更新
                if (!strataMap.has(compositeKey) || seq < strataMap.get(compositeKey).seq) {
                  strataMap.set(compositeKey, {
                    qduecd: qduecd,
                    tong: tong,
                    seq: seq,
                    lower_age: lower_age,
                    upper_age: upper_age,
                    compositeKey: compositeKey  // 存储复合键用于后续处理
                  });
                }
              }
            });
            
            // 转换为数组并按 seq 升序排序
            this.qduecdList = Array.from(strataMap.values())
              .sort((a, b) => a.seq - b.seq);
            
            // 新增：跳转到缓冲区范围
            if (dataSource) {
              this.viewer.flyTo(dataSource, {
                duration: 2, // 2秒动画
                offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), 0)
              });
            }
            
            // 新增：隐藏用户菜单（轨迹列表）
            this.showUserMenu = false;
            
            this.closeBufferModal();
          } else {
            alert(response.data.msg || '地质数据查询失败');
          }
        } catch (error) {
          // 如果是取消请求导致的错误，不显示错误信息
          if (!axios.isCancel(error)) {
            console.error('地质数据查询失败:', error);
            alert('地质数据查询失败，请检查网络连接');
          }
        } finally {
          // 无论成功失败都关闭加载状态
          this.bufferLoading = false;
          this.cancelTokenSource = null;
        }
      },
      
      // 新增：取消缓冲区加载
      cancelBufferLoading() {
        if (this.cancelTokenSource) {
          // 取消请求
          this.cancelTokenSource.cancel('用户取消了计算');
        }
        this.bufferLoading = false;
        this.closeBufferModal();
      },


      // 新增：取消缓冲区加载
      cancelAreaLoading() {
        if (this.cancelTokenSource) {
          // 取消请求
          this.cancelTokenSource.cancel('用户取消了计算');
        }
        this.areaLoading = false;
        // this.closeBufferModal();
      },
      
      // 删除轨迹
      async deleteTrack(track) {
        if (!confirm(`确定要删除轨迹【${track.name}】吗？`)) return;
        
        try {
          const token = localStorage.getItem('userToken');
          const response = await axios.get(
            `https://geologymine.fun/track/deleteTrack?trackId=${track.id}`,
            {
              headers: {
                'Token': token
              }
            }
          );
          
          if (response.data.errcode === 0) {
            // 从当前列表中移除
            this.userTracks = this.userTracks.filter(t => t.id !== track.id);
            this.trackPagination.total -= 1;
            
            // 如果删除的是当前页最后一项且不是第一页
            if (this.userTracks.length === 0 && this.trackPagination.currentPage > 1) {
              this.trackPagination.currentPage -= 1;
              this.fetchUserTracks();
            }
          } else {
            alert(response.data.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除轨迹失败:', error);
          alert('删除失败，请检查网络连接');
        }
      },


      async loadGeologyByPolygon(area) {
        // 创建取消令牌源
        this.cancelTokenSource = axios.CancelToken.source();

        // 设置查询类型
        this.currentQueryType = 'uploadPolygon';

        this.currentAreaId = area.groupid;

        this.areaLoading = true;
        
        try {
          // 清除旧地质数据
          if (this.currentGeologyDataSource) {
            this.viewer.dataSources.remove(this.currentGeologyDataSource);
          }

          
          
          // 调用接口
          const response = await axios.get(
            'https://geologymine.fun/geotools/getGeologyFileByPolygonId',
            {
              params: {
                groupId: area.groupid,
              },
              cancelToken: this.cancelTokenSource.token // 添加取消令牌
            }
          );
          
          if (response.data.errcode === 0) {
            // 添加地质数据到地图
            const dataSource = this.addGeoJsonToCesium(response.data.body.geojsonFeaturecollection);
            
            // 提取所有地层代码(qduecd)并去重
            const qduecdSet = new Set();
            const geojson = JSON.parse(response.data.body.geojsonFeaturecollection);
            // 修改地层信息提取方式 - 使用复合键(qduecd+tong)去重
            const strataMap = new Map();
            geojson.features.forEach(feature => {
              if (feature.properties && feature.properties.qduecd && feature.properties.tong) {
                const qduecd = feature.properties.qduecd;
                const tong = feature.properties.tong;
                const seq = parseInt(feature.properties.seq) || 0;
                const lower_age = feature.properties.lower_age;
                const upper_age = feature.properties.upper_age;
                
                // 创建复合键：qduecd + tong
                const compositeKey = `${qduecd}_${tong}`;
                
                // 如果地层代码不存在或当前特征的seq更小（更老），则更新
                if (!strataMap.has(compositeKey) || seq < strataMap.get(compositeKey).seq) {
                  strataMap.set(compositeKey, {
                    qduecd: qduecd,
                    tong: tong,
                    seq: seq,
                    lower_age: lower_age,
                    upper_age: upper_age,
                    compositeKey: compositeKey  // 存储复合键用于后续处理
                  });
                }
              }
            });
            
            // 转换为数组并按 seq 升序排序
            this.qduecdList = Array.from(strataMap.values())
              .sort((a, b) => a.seq - b.seq);
            
            // 新增：跳转到缓冲区范围
            if (dataSource) {
              this.viewer.flyTo(dataSource, {
                duration: 2, // 2秒动画
                offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), 0)
              });
            }
            
            // 新增：隐藏用户菜单（轨迹列表）
            this.showUserMenu = false;
            
            this.closeBufferModal();
          } else {
            alert(response.data.msg || '地质数据查询失败');
          }
        } catch (error) {
          // 如果是取消请求导致的错误，不显示错误信息
          if (!axios.isCancel(error)) {
            console.error('地质数据查询失败:', error);
            alert('地质数据查询失败，请检查网络连接');
          }
        } finally {
          // 无论成功失败都关闭加载状态
          this.areaLoading = false;
          this.cancelTokenSource = null;
        }
      },



      // 定位到轨迹
      async flyToTrack(track) {
        try {
          // 清除旧轨迹
          this.clearCurrentTrack();

          // 获取用户token
          const token = localStorage.getItem('userToken');
          if (!token) {
            alert('请先登录');
            this.showLoginModal = true;
            return;
          }

          // 调用新接口获取轨迹数据
          const response = await axios.get(
            `https://geologymine.fun/track/getTrackGeomById?trackId=${track.id}`,
            {
              headers: {
                'Token': token
              }
            }
          );

          if (response.data.errcode === 0 && response.data.body) {
            const trackData = response.data.body;
            
            // 解析轨迹数据
            const geom = JSON.parse(trackData.geom);
            if (geom.type !== 'LineString' || !geom.coordinates || geom.coordinates.length < 2) {
              alert('无效的轨迹数据');
              return;
            }

            // 计算轨迹的边界范围
            let minLon = 180, maxLon = -180, minLat = 90, maxLat = -90;
            geom.coordinates.forEach(coord => {
              const [lon, lat] = coord;
              minLon = Math.min(minLon, lon);
              maxLon = Math.max(maxLon, lon);
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
            });

            // 飞向轨迹范围
            this.viewer.camera.flyTo({
              destination: Cesium.Rectangle.fromDegrees(minLon, minLat, maxLon, maxLat),
              orientation: {
                heading: Cesium.Math.toRadians(0),
                pitch: Cesium.Math.toRadians(-90),
                roll: 0.0
              }
            });

            // 在地图上显示轨迹
            this.displayTrackOnMap(track, geom);

            // 显示轨迹清除按钮，隐藏标记点清除按钮
            this.showTrackClearButton = true;
            // this.showMarkerClearButton = false;
          } else {
            alert(response.data.msg || '获取轨迹数据失败');
          }
        } catch (e) {
          console.error('轨迹获取或解析失败', e);
          alert('轨迹获取或解析失败');
        }
      },

      // 在地图上显示轨迹
      displayTrackOnMap(track, geom) {
        const positions = geom.coordinates.map(coord => 
          Cesium.Cartesian3.fromDegrees(coord[0], coord[1])
        );
        
        // 创建轨迹实体
        this.currentTrackEntity = this.viewer.entities.add({
          name: track.name,
          polyline: {
            positions: positions,
            width: 5,
            material: new Cesium.PolylineGlowMaterialProperty({
              glowPower: 0.2,
              color: Cesium.Color.PINK
            })
          }
        });
        
        // 存储当前轨迹ID
        this.currentTrackId = track.id;
      },


      updateRectangleOnMap() {
        let { minLon, minLat, maxLon, maxLat } = this.rectangle;
        const west = Math.min(minLon, maxLon);
        const east = Math.max(minLon, maxLon);
        const south = Math.min(minLat, maxLat);
        const north = Math.max(minLat, maxLat);

        if (this.rectangleEntity) {
          this.viewer.entities.remove(this.rectangleEntity);
        }

        this.rectangleEntity = this.viewer.entities.add({
          name: "选择区域",
          rectangle: {
            coordinates: Cesium.Rectangle.fromDegrees(west, south, east, north),
            fill: false, // 不填充颜色
            outline: true, // 启用边框
            outlineColor: Cesium.Color.WHITE // 设置边框颜色为蓝色
          }
        });
      },
      clearQduecdList() {
        this.qduecdList = [];

        // 清除选择的地层数据
        if (this.selectedGeologyDataSource) {
          this.viewer.dataSources.remove(this.selectedGeologyDataSource);
          this.selectedGeologyDataSource = null;
        }
        
        // 清除查询类型
        this.currentQueryType = null;

        // 清除地质数据源
        if (this.currentDataSource) {
          this.viewer.dataSources.remove(this.currentDataSource);
        }
        if (this.currentDataSourceRectangle) {
          this.viewer.dataSources.remove(this.currentDataSourceRectangle);
        }
        
        // 清除矩形
        if (this.rectangleEntity) {
          this.viewer.entities.remove(this.rectangleEntity);
          this.rectangleEntity = null;
        }
        
        // 清除轨迹实体和轨迹ID
        if (this.currentTrackEntity) {
          this.viewer.entities.remove(this.currentTrackEntity);
          this.currentTrackEntity = null;
        }
        this.currentTrackId = null; // 重置轨迹ID

        this.currentStratumInfo = null;
        
        // 重置矩形状态
        this.rectangle = {
          minLon: null,
          minLat: null,
          maxLon: null,
          maxLat: null
        };
      },

      // 处理地层点击
      async handleQduecdClick(item) {
        try {
          // 清除之前选中的地层
          if (this.selectedGeologyDataSource) {
            this.viewer.dataSources.remove(this.selectedGeologyDataSource);
            this.selectedGeologyDataSource = null;
          }
          
          // 设置当前选中的地层
          this.selectedQduecd = item.qduecd;
          this.currentStratumInfo = item;
          
          let apiUrl, params, response;

          // 根据当前查询类型调用不同API
          if (this.currentQueryType === 'rectangle') {
            // 矩形查询
            apiUrl = 'https://geologymine.fun/geotools/getGeologyInfoByRectangle';
            params = {
              minLon: this.currentRectangle.west,
              minLat: this.currentRectangle.south,
              maxLon: this.currentRectangle.east,
              maxLat: this.currentRectangle.north,
              keywords: item.qduecd,
              tong: item.tong
            };
            response = await axios.post(apiUrl, params);
          } 
          else if (this.currentQueryType === 'buffer') {
            // 缓冲区查询
            apiUrl = 'https://geologymine.fun/track/getGeologyFileByTrackBufferName';
            params = {
              keyWords: item.qduecd,
              trackId: this.currentTrackId,
              buffer: this.bufferDistance,
              tong: item.tong
            };
            response = await axios.get(apiUrl, { params });
          }
          else if (this.currentQueryType === 'polygon') {
            // 多边形查询
            apiUrl = 'https://geologymine.fun/geotools/getGeologyFileByPolygonName';
            params = {
              keywords: item.qduecd,
              polygonGeoJSON: this.currentPolygonGeoJSON, // 传递之前存储的多边形
              tong: item.tong
            };
            response = await axios.post(apiUrl, params);
          }
          else if (this.currentQueryType === 'uploadPolygon') {
            // 缓冲区查询
            apiUrl = 'https://geologymine.fun/geotools/getGeologyFileByPolygonIdName';
            params = {
              keyword: item.qduecd,
              tong: item.tong,
              groupId: this.currentAreaId
            };
            response = await axios.get(apiUrl, { params });
          }
          else {
            alert('请先进行地质查询');
            return;
          }

          if (response.data.errcode === 0) {
            // 根据tong值获取颜色
            const color = this.qduecdColorMap[item.tong] || '#cdfd37';
            
            // 添加新地质数据并应用颜色
            const dataSource = await this.addSelectedGeoJsonToCesium(
              response.data.body.geojsonFeaturecollection,
              color
            );
            
            // 保存数据源以便后续清除
            this.selectedGeologyDataSource = dataSource;
            
            // 跳转到选中地层范围
            if (dataSource) {
              this.viewer.flyTo(dataSource, {
                duration: 1.5, // 1.5秒动画
                offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), 0)
              });
            }
          } else {
            alert(response.data.msg || '地质数据查询失败');
          }
        } catch (error) {
          console.error('地质信息查询失败:', error);
          alert('查询失败，请检查网络连接');
        }
      },

      // 删除标记点
      async deletePoi(poi) {
        if (!confirm(`确定要删除标记点【${poi.name}】吗？`)) return;

        try {
          const token = localStorage.getItem('userToken');
          const response = await axios.get(
            `https://geologymine.fun/images/deletePoi?markerId=${poi.id}`,
            {
              headers: {
                'Token': token
              }
            }
          );

          if (response.data.errcode === 0) {
            // 前端直接过滤掉已删除项
            this.userPois = this.userPois.filter(p => p.id !== poi.id);
            this.poiPagination.total -= 1;
            
            // 如果删除的是当前显示的最后一个条目
            if (this.userPois.length === 0 && this.poiPagination.currentPage > 1) {
              this.poiPagination.currentPage -= 1;
              this.fetchUserPois();
            }
          } else {
            alert(response.data.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          alert('删除失败，请检查网络连接');
        }
      },


      // 打开坐标输入框时自动聚焦
      openCoordinateInput() {
        this.$nextTick(() => {
          this.$refs.coordInput.focus()
        })
      },
      // 处理坐标确认
      handleCoordinateConfirm() {
        this.inputError = ''
        const input = this.coordinateInput.trim()

        // 格式验证
        if (!this.isValidCoordinate(input)) {
          this.inputError = '格式错误！示例：116.397428,39.90923'
          return
        }

        // 数值范围验证
        const [lon, lat] = input.split(',').map(Number)
        if (Math.abs(lon) > 180 || Math.abs(lat) > 90) {
          this.inputError = '经纬度超出有效范围（经度±180°，纬度±90°）'
          return
        }

        // 执行跳转
        this.flyToCoordinate(lon, lat)
        this.showCoordinateModal = false
      },

      // 经纬度格式验证
      isValidCoordinate(input) {
        return /^[-+]?\d{1,3}(\.\d+)?,\s*[-+]?\d{1,2}(\.\d+)?$/.test(input)
      },

      // 执行飞行动画
      flyToCoordinate(lon, lat) {
        this.viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(lon, lat, 5000),
          orientation: {
            heading: Cesium.Math.toRadians(0),
            pitch: Cesium.Math.toRadians(-90),
            roll: 0.0
          }
        })
      },

      clearCurrentPoiMarker() {
        if (this.currentPoiMarker) {
          this.viewer.entities.remove(this.currentPoiMarker);
          this.currentPoiMarker = null;
        }
      },
      


      // 切换坐标拾取模式
      toggleCoordinatePick() {
        this.isCoordinatePickActive = !this.isCoordinatePickActive
        if (!this.isCoordinatePickActive) {
          this.pickedCoordinates = null
        }
      },

      // 复制坐标到剪贴板
      copyCoordinates() {
        const text = `${this.pickedCoordinates.longitude}, ${this.pickedCoordinates.latitude}`
        // navigator clipboard 需要https等安全上下文
        if (navigator.clipboard && window.isSecureContext) {
                // navigator clipboard 向剪贴板写文本
                return navigator.clipboard.writeText(text);
            } else {
                // 创建text area
                let textArea = document.createElement("textarea");
                textArea.value = text;
                // 使text area不在viewport，同时设置不可见
                textArea.style.position = "absolute";
                textArea.style.opacity = 0;
                textArea.style.left = "-999999px";
                textArea.style.top = "-999999px";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                return new Promise((res, rej) => {
                    // 执行复制命令并移除文本框
                    document.execCommand('copy') ? res() : rej();
                    textArea.remove();
                });
            }
      },


      // 切换到邮箱登录
      switchToMailLogin() {
        this.activeForm = 'mailLogin'
        this.showLoginModal = true
      },

      // 发送邮箱验证码
      async sendMailCaptcha() {
        if (!this.validateEmail(this.mailLoginForm.email)) {
          alert('请输入有效的邮箱地址')
          return
        }

        this.isSendingCaptcha = true
        try {
          const response = await axios.get(
            `https://geologymine.fun/user/user/sendCaptcha?to=${this.mailLoginForm.email}`
          )
          
          if (response.data.errcode === 0) {
            this.startCaptchaCountdown()
            // alert(response.data.description || '验证码已成功发送！')
          } else {
            alert(response.data.description || '验证码发送失败')
          }
        } catch (error) {
          console.error('发送验证码失败:', error)
          alert('验证码发送失败，请稍后重试')
        } finally {
          this.isSendingCaptcha = false
        }
      },

      // 开始倒计时
      startCaptchaCountdown() {
        this.captchaCountdown = 60
        const timer = setInterval(() => {
          if (this.captchaCountdown <= 0) {
            clearInterval(timer)
            return
          }
          this.captchaCountdown--
        }, 1000)
      },

      // 邮箱登录
      async handleMailLogin() {
        if (!this.validateEmail(this.mailLoginForm.email)) {
          alert('请输入有效的邮箱地址')
          return
        }

        if (!this.mailLoginForm.verifyCode) {
          alert('请输入验证码')
          return
        }

        try {
          const response = await axios.post(
            'https://geologymine.fun/user/user/loginWithMail',
            {
              email: this.mailLoginForm.email,
              verifyCode: this.mailLoginForm.verifyCode
            }
          )

          if (response.data.code === 0) {
            // 保持与普通登录相同的处理逻辑
            this.isLoggedIn = true
            this.userInfo = {
              username: response.data.data.username,
              email: this.mailLoginForm.email,
              avatar: response.data.data.avatar || ''
            }

            // 存储token
            const token = response.data.data
            if (token) {
              localStorage.setItem('userToken', token)
              axios.defaults.headers.common['token'] = token
            }

            this.showLoginModal = false
            this.mailLoginForm = { email: '', verifyCode: '' }
          } else {
            alert(response.data.description || '登录失败')
          }
        } catch (error) {
          console.error('邮箱登录失败:', error)
          alert('登录失败，请检查验证码')
        }
      },

      // 邮箱格式验证
      validateEmail(email) {
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
      },

      // 关闭登录模态框时重置表单
      closeLoginModal() {
        this.showLoginModal = false
        this.activeForm = 'login' // 重置为默认登录表单
        // 清空所有表单
        this.loginForm.userPassword = ''
        this.registerForm = {
          userAccount: '',
          userPassword: '',
          checkPassword: '',
          phoneNumber: '',
          userName: '',
          email: ''
        }
        this.mailLoginForm = {
          email: '',
          verifyCode: ''
        }
      },
      // 处理注册
      async handleRegister() {
        // 去除首尾空格
        this.registerForm.userPassword = this.registerForm.userPassword.trim();
        this.registerForm.checkPassword = this.registerForm.checkPassword.trim();

        // 邮箱格式正则验证
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(this.registerForm.email)) {
          alert('请输入有效的邮箱地址');
          return;
        }

        // 在原有验证基础上添加空格检查
        if (/(\s)/.test(this.registerForm.userPassword)) {
          alert('密码不能包含空格');
          return;
        }

        // 保持原有验证逻辑...
        if (this.registerForm.userPassword !== this.registerForm.checkPassword) {
          alert('两次输入的密码不一致');
          return;
        }

        // 表单验证
        if (!this.registerForm.userAccount || 
            !this.registerForm.userPassword ||
            !this.registerForm.checkPassword ||
            !this.registerForm.phoneNumber ||
            !this.registerForm.userName ||
            !this.registerForm.email) {
          alert('请填写所有必填项')
          return
        }

        if (this.registerForm.userPassword !== this.registerForm.checkPassword) {
          alert('两次输入的密码不一致')
          return
        }

        if (!/^1[3-9]\d{9}$/.test(this.registerForm.phoneNumber)) {
          alert('请输入有效的手机号码')
          return
        }

        try {
          const response = await axios.post(
            'https://geologymine.fun/user/user/register',
            this.registerForm
          )

          if (response.data.code === 0) {
            alert('注册成功，请登录')
            this.showRegisterForm = false
            this.registerForm = {
              userAccount: '',
              userPassword: '',
              checkPassword: '',
              phoneNumber: '',
              userName: '',
              email: ''
            }
          } else {
            alert(response.data.description || '注册失败')
          }
        } catch (error) {
          console.error('注册错误:', error)
          if (error.response) {
            alert(`注册失败: ${error.response.data.msg}`)
          } else {
            alert('注册失败，请检查网络连接')
          }
        }
      },
      async searchUserPois() {
        this.poiPagination.currentPage = 1; // 搜索时重置到第一页
        await this.fetchUserPois();
      },
      
      // 清除搜索
      clearPoiSearch() {
        this.poiSearchKeyword = '';
        this.searchUserPois();
      },
      parseLonLat() {
        const lonLat = this.newPoiData.lonLat.trim();
        if (!lonLat) {
          alert('请输入有效的经纬度');
          return null;
        }

        const [lon, lat] = lonLat.split(',').map(Number);
        if (isNaN(lon) || isNaN(lat)) {
          alert('请输入有效的经纬度格式，例如：120.123456,30.678901');
          return null;
        }

        return { lon, lat };
      },
      // 获取图片URL
      getImageUrl(filePath) {
        if (!filePath) return '';
        const filename = filePath.split('\\').pop(); // 处理Windows路径
        // return `https://geologymine.fun/images/getPoiImage?fileName=${encodeURIComponent(filename)}`;
        return `https://geologymine.fun/images/getPoiThumbnailImage?fileName=${encodeURIComponent(filename)}`;
      },

      // 获取图片URL
      getImageDetailUrl(filePath) {
        if (!filePath) return '';
        const filename = filePath.split('\\').pop(); // 处理Windows路径
        return `https://geologymine.fun/images/getPoiImage?fileName=${encodeURIComponent(filename)}`;
        // return `https://geologymine.fun/images/getPoiThumbnailImage?fileName=${encodeURIComponent(filename)}`;
      },
      
      // 图片加载失败处理
      handlePoiImageError(poi) {
        // 可以在这里设置默认图片或标记图片加载失败
        console.error(`图片加载失败: ${poi.filePath}`);
        const imgElement = event.target;
        imgElement.src = '/marker.png'; // 设置默认图片
      },
      
      // 预览大图
      previewPoiImage(poi) {
        if (poi.filePath) {
          this.$emit('show-image', this.getImageDetailUrl(poi.filePath));
          // 或者使用更复杂的图片查看器
          window.open(this.getImageDetailUrl(poi.filePath), '_blank');
        }
      },
      // 进入添加POI模式
      startAddPoiMode() {
        // 确保用户已登录
        if (!this.isLoggedIn) {
          alert('请先登录后再添加标记点');
          this.showLoginModal = true;
          return;
        }
  
        // 保存原始点击处理器
        if (!this.originalClickHandler) {
          this.originalClickHandler = this.handler.getInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
        }
  
        this.isAddPoiMode = true;
        this.showPoiForm = true;
        this.newPoiData = {
          name: '',
          description: '',
          lon: null,
          lat: null
        };
  
        // 移除之前的临时标记
        if (this.tempPoiMarker) {
          this.viewer.entities.remove(this.tempPoiMarker);
          this.tempPoiMarker = null;
        }
  
        // 移除原始点击处理器
        this.handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
        
        // 设置新的点击处理器用于添加POI
        this.handler.setInputAction((movement) => {
          const cartesian = this.viewer.scene.pickPosition(movement.position);
          if (!cartesian) return;
          
          const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
          const longitude = Cesium.Math.toDegrees(cartographic.longitude);
          const latitude = Cesium.Math.toDegrees(cartographic.latitude);
          
          this.newPoiData.lonLat = `${longitude},${latitude}`;
          
          if (this.tempPoiMarker) {
            this.viewer.entities.remove(this.tempPoiMarker);
          }
          
          this.tempPoiMarker = this.viewer.entities.add({
            position: cartesian,
            billboard: {
              image: '/marker.png',
              width: 32,
              height: 32,
              color: Cesium.Color.YELLOW,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              disableDepthTestDistance: Number.POSITIVE_INFINITY
            },
            label: {
              text: '新标记点',
              font: '14pt sans-serif',
              style: Cesium.LabelStyle.FILL_AND_OUTLINE,
              outlineWidth: 2,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              pixelOffset: new Cesium.Cartesian2(0, -32),
              fillColor: Cesium.Color.YELLOW,
              showBackground: true,
              backgroundColor: new Cesium.Color(0.1, 0.1, 0.1, 0.7)
            }
          });
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      },

      changePage(page, event) {
        if (event) event.stopPropagation(); // 阻止事件冒泡
        
        if (page < 1 || page > this.poiPagination.totalPages || page === this.poiPagination.currentPage) {
          return;
        }
        this.poiPagination.currentPage = page;
        this.fetchUserPois();
      },
  
      handleImageUpload(event) {
        const file = event.target.files[0];
        if (file) {
          this.newPoiData.imageFile = file;
          
          // Create preview
          const reader = new FileReader();
          reader.onload = (e) => {
            this.newPoiData.imagePreview = e.target.result;
          };
          reader.readAsDataURL(file);
        }
      },

      getPageNumbers() {
        const current = this.poiPagination.currentPage;
        const total = this.poiPagination.totalPages;
        const range = 2; // 显示当前页前后各2页

        let start = Math.max(1, current - range);
        let end = Math.min(total, current + range);

        // 确保显示足够的页码
        if (current - 1 < range) {
          end = Math.min(total, end + (range - (current - 1)));
        }
        if (total - current < range) {
          start = Math.max(1, start - (range - (total - current)));
        }

        const pages = [];
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        return pages;
      },
      
      removeImage() {
        this.newPoiData.imageFile = null;
        this.newPoiData.imagePreview = null;
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }
      },
      
      // 保存POI
      async savePoi() {
        if (!this.newPoiData.name || !this.newPoiData.lonLat) {
          alert('请填写完整信息');
          return;
        }

        const { lon, lat } = this.parseLonLat();
        if (!lon || !lat) {
          return;
        }

        this.newPoiData.lon = lon;
        this.newPoiData.lat = lat;

        try {
          const token = localStorage.getItem('userToken');
          const formData = new FormData();

          // Add POI data to formData
          formData.append('lon', this.newPoiData.lon);
          formData.append('lat', this.newPoiData.lat);
          formData.append('name', this.newPoiData.name);
          formData.append('description', this.newPoiData.description || '');

          // Add image if exists
          if (this.newPoiData.imageFile) {
            formData.append('image', this.newPoiData.imageFile);
          }

          const response = await axios.post('https://geologymine.fun/images/savePoi', formData, {
            headers: {
              'Token': token,
              'Content-Type': 'multipart/form-data'
            }
          });

          if (response.data.errcode === 0) {
            alert('保存成功');
            this.poiPagination.currentPage = 1; // 重置到第一页
            this.fetchUserPois(); // 重新加载数据
            this.cancelAddPoi();
          } else {
            alert(response.data.msg || '保存失败');
          }
        } catch (error) {
          console.error('保存POI失败:', error);
          alert('保存失败，请检查网络');
        }
      },

      // 取消添加
      cancelAddPoi() {
        this.isAddPoiMode = false;
        this.showPoiForm = false;
  
        // 移除POI模式的点击处理器
        this.handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
        
        // 恢复原始点击处理器
        if (this.originalClickHandler) {
          this.handler.setInputAction(
            this.originalClickHandler,
            Cesium.ScreenSpaceEventType.LEFT_CLICK
          );
        }
  
        // 移除临时标记
        if (this.tempPoiMarker) {
          this.viewer.entities.remove(this.tempPoiMarker);
          this.tempPoiMarker = null;
        }
      },
  

      // 切换用户菜单
      toggleUserMenu(event) {
        // 阻止事件冒泡
        event.stopPropagation();
        this.showUserMenu = !this.showUserMenu;

        if (!this.showUserMenu) {
          this.clearCurrentPoiMarker();
        }
        
        // 如果打开菜单且是登录状态，加载数据
        if (this.showUserMenu && this.isLoggedIn) {
          this.activeTab = 'poi'; // 默认显示标记点
          this.fetchUserPois(); // 加载标记点数据
          this.fetchUserTracks();
          this.fetchUserAreas();
        }
      },


      loadMorePois() {
        if (!this.userPoisLoading && this.poiPagination.hasMore) {
          this.poiPagination.currentPage += 1;
          this.fetchUserPois();
        }
      },
      
      // 获取用户POI列表
      async fetchUserPois() {
        this.userPoisLoading = true;
        try {
          const token = localStorage.getItem('userToken');
          const params = {
            currentPage: this.poiPagination.currentPage,
            pageSize: this.poiPagination.pageSize
          };
          
          // 如果有搜索关键词，使用搜索接口
          const apiUrl = this.poiSearchKeyword 
            ? 'https://geologymine.fun/images/getPoisByName'
            : 'https://geologymine.fun/images/getPois';
          
          // 如果有搜索关键词，添加搜索参数
          if (this.poiSearchKeyword) {
            params.poiName = this.poiSearchKeyword;
          }
          
          const response = await axios.get(apiUrl, {
            headers: {
              'Token': token
            },
            params: params
          });

          if (response.data.errcode === 0) {
            this.userPois = response.data.body.data.map(poi => ({
              ...poi,
              imageUrl: poi.filePath ? this.getImageUrl(poi.filePath) : null
            }));

            // 更新分页信息
            this.poiPagination.total = response.data.body.total || 0;
            this.poiPagination.totalPages = Math.ceil(this.poiPagination.total / this.poiPagination.pageSize);
          } else {
            alert(response.data.msg || '获取标记点失败');
          }
        } catch (error) {
          console.error('获取用户POI列表失败:', error);
          if (error.response && error.response.status === 401) {
            this.logout();
          }
          alert('获取标记点失败，请检查网络');
        } finally {
          this.userPoisLoading = false;
        }
      },

      // 处理全局点击事件
      handleGlobalClick(event) {
        // 检查点击是否发生在用户菜单内（标记点列表区域）
        const userMenu = this.$el.querySelector('.user-menu');
        const isClickInMenu = userMenu && userMenu.contains(event.target);
        
        // 检查点击是否发生在用户头像上
        const userAvatar = this.$el.querySelector('.user-avatar');
        const isClickOnAvatar = userAvatar && userAvatar.contains(event.target);

        // 如果点击发生在头像，则清除标记点和轨迹
        if (isClickOnAvatar) {
          // this.clearUserPoiMarker();
          this.clearCurrentTrack(); // 新增：清除轨迹
        }
        
        // 如果点击发生在菜单外且不在头像上，清除标记点
        // if (!isClickInMenu && !isClickOnAvatar) {
        //   this.clearUserPoiMarker();
        // }
      },

      // 清除用户标记点
      clearUserPoiMarker() {
        if (this.userPoiMarker) {
          this.viewer.entities.remove(this.userPoiMarker);
          this.userPoiMarker = null;
        }
      },

      
      // 跳转到用户标记点（修改后）
      flyToUserPoi(poi) {
        try {
          // 清除之前的标记点
          this.clearCurrentMarker();
          
          const geom = JSON.parse(poi.geom);
          if (geom.type === 'Point' && geom.coordinates.length >= 2) {
            const [longitude, latitude] = geom.coordinates;
            
            // 创建新标记点
            this.userPoiMarker = this.viewer.entities.add({
              name: poi.name || '用户标记点',
              position: Cesium.Cartesian3.fromDegrees(longitude, latitude, 100),
              billboard: {
                image: '/marker.png',
                width: 32,
                height: 32,
                color: Cesium.Color.YELLOW,
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
              }
            });

            // 飞向该点
            this.viewer.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(longitude, latitude, 3000),
              orientation: {
                heading: Cesium.Math.toRadians(0),
                pitch: Cesium.Math.toRadians(-90),
                roll: 0.0
              }
            });

            // 显示标记点清除按钮，隐藏轨迹清除按钮
            this.showMarkerClearButton = true;
            // this.showTrackClearButton = false;
          }
        } catch (e) {
          console.error('POI定位失败:', e);
        }
      },
      
      // 格式化时间
      formatTime(timeStr) {
        if (!timeStr) return '';
        const date = new Date(timeStr);
        return date.toLocaleString();
      },

      resetPoiPagination() {
        this.poiPagination = {
          currentPage: 1,
          pageSize: 3,
          total: 0,
          hasMore: true
        };
      },
  
  
      //登录相关
      // 新增登录相关方法
      async handleLogin() {
        if (!this.loginForm.userAccount || !this.loginForm.userPassword) {
          alert('请输入用户名和密码');
          return;
        }
  
        try {
          const response = await axios.post('https://geologymine.fun/user/user/login', this.loginForm);
          
          if (response.data.data !== "error") {
            this.isLoggedIn = true;
            this.userInfo = {
              username: response.data.data.username || this.loginForm.userAccount,
              email: response.data.data.email || '',
              avatar: response.data.data.avatar || ''
            };
            
            // 1. 存储token到本地
            const token = response.data.data;
            if (token) {
              localStorage.setItem('userToken', token);
              
              // 2. 设置axios默认请求头
              axios.defaults.headers.common['token'] = `${token}`;
            }
            
            // 3. 清空密码并关闭弹窗
            this.loginForm.userPassword = '';
            this.showLoginModal = false;
            
            // alert('登录成功');
          } else {
            alert('登录失败，请检查用户名或密码');
          }
        } catch (error) {
          console.error('登录错误:', error);
          alert('登录失败，请检查网络连接');
        }
      },
          
      logout() {
        this.isLoggedIn = false;
        this.userInfo = {
          username: '',
          email: '',
          avatar: ''
        };
        localStorage.removeItem('userToken');
        this.showUserMenu = false;
        
        // 清除标记点
        if (this.userPoiMarker) {
          this.viewer.entities.remove(this.userPoiMarker);
          this.userPoiMarker = null;
        }
      },
      
      // 检查本地是否有token
      checkLoginStatus() {
        const token = localStorage.getItem('userToken');
        if (token) {
          // 这里可以添加验证token有效性的API调用
          this.isLoggedIn = true;
          // 获取用户信息
          this.fetchUserInfo();
        }
      },
      
      async fetchUserInfo() {
        try {
          const response = await axios.get('https://geologymine.fun/user/user/info', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('userToken')}`
            }
          });
          
          if (response.data.code === 200) {
            this.userInfo = {
              username: response.data.data.username || '用户',
              email: response.data.data.email || '',
              avatar: response.data.data.avatar || ''
            };
          }
        } catch (error) {
          console.error('获取用户信息失败:', error);
          this.logout();
        }
      },
  
      // POI搜索相关方法
      searchPoi() {
        if (!this.searchKeyword.trim()) {
          alert('请输入搜索关键词');
          return;
        }
  
        const apiUrl = `https://geologymine.fun/geotools/searchPoi?keyWords=${encodeURIComponent(this.searchKeyword)}&key=a`;
        
        this.clearPoiMarkers();
        this.poiResults = [];
        
        axios.get(apiUrl)
          .then(response => {
            if (response.data.errcode === 0 && response.data.body && response.data.body.data) {
              this.poiResults = response.data.body.data.map(item => {
                const [lon, lat] = item.location.split(',').map(Number);
                return {
                  ...item,
                  lon: lon,
                  lat: lat
                };
              });
              this.addPoiMarkers(this.poiResults.slice(0, 6));
            } else {
              alert(response.data.msg || '未找到相关POI结果');
            }
          })
          .catch(error => {
            console.error("POI搜索错误:", error);
            alert("POI搜索失败，请检查网络或接口地址！");
          });
      },
      
      addPoiMarkers(pois) {
        this.clearPoiMarkers();
        
        pois.forEach(poi => {
          const marker = this.viewer.entities.add({
            name: poi.name,
            position: Cesium.Cartesian3.fromDegrees(poi.lon, poi.lat),
            point: {
              pixelSize: 12,
              color: Cesium.Color.BLUE,
              outlineColor: Cesium.Color.WHITE,
              outlineWidth: 2
            },
            label: {
              text: poi.name,
              font: '14pt sans-serif',
              style: Cesium.LabelStyle.FILL_AND_OUTLINE,
              outlineWidth: 2,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              pixelOffset: new Cesium.Cartesian2(0, -10),
              fillColor: Cesium.Color.YELLOW,
              showBackground: true,
              backgroundColor: new Cesium.Color(0.1, 0.1, 0.1, 0.7)
            }
          });
          this.poiMarkers.push(marker);
        });
      },
      
      clearPoiMarkers() {
        this.poiMarkers.forEach(marker => {
          this.viewer.entities.remove(marker);
        });
        this.poiMarkers = [];
      },
      
      clearSearch() {
        this.searchKeyword = '';
        this.poiResults = [];
        this.clearPoiMarkers();
      },
      
      flyToPoi(poi) {
        this.viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(poi.lon, poi.lat, 5000),
          orientation: {
            heading: Cesium.Math.toRadians(0),
            // pitch: Cesium.Math.toRadians(-30),
            roll: 0.0
          }
        });
      },
      
      showPhoto(photos) {
        // 这里可以实现照片画廊功能
        console.log('POI照片:', photos);
        alert(`查看POI照片，共${photos.length}张`);
      },
  
      // 其他原有方法保持不变...
      initRoadNetworkLayer() {
        this.roadNetworkProvider = new Cesium.UrlTemplateImageryProvider({
          url: 'https://webst01.is.autonavi.com/appmaptile?style=8&x={x}&y={y}&z={z}',
          minimumLevel: 3,
          maximumLevel: 18,
          credit: '高德地图路网'
        });
      },
  
      initTerrainProvider() {
        this.terrainProvider = new Cesium.UrlTemplateImageryProvider({
          url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
          minimumLevel: 0,
          maximumLevel: 19,
          credit: 'ArcGIS World Topo Map',
          tilingScheme: new Cesium.WebMercatorTilingScheme(),
          tileWidth: 256,
          tileHeight: 256
        });
        
        this.baseImageryProvider = this.viewer.imageryLayers.get(0).imageryProvider;
      },
      
      toggleTerrain() {
        try {
          this.useTerrainMap = !this.useTerrainMap;
          
          if (this.useTerrainMap) {
            const layers = this.viewer.imageryLayers;
            layers.remove(layers.get(0), false);
            layers.addImageryProvider(this.terrainProvider, 0);
          } else {
            const layers = this.viewer.imageryLayers;
            layers.remove(layers.get(0), false);
            layers.addImageryProvider(this.baseImageryProvider, 0);
          }
          
          if (this.showRoadNetwork && !this.roadNetworkLayer) {
            this.roadNetworkLayer = this.viewer.imageryLayers.addImageryProvider(this.roadNetworkProvider);
          }
        } catch (error) {
          console.error("地形切换失败:", error);
          this.useTerrainMap = !this.useTerrainMap;
        }
      },
      
      toggleRoadNetwork() {
        this.showRoadNetwork = !this.showRoadNetwork;
        if (this.showRoadNetwork) {
          if (!this.roadNetworkLayer) {
            this.roadNetworkLayer = this.viewer.imageryLayers.addImageryProvider(this.roadNetworkProvider);
          }
        } else {
          if (this.roadNetworkLayer) {
            this.viewer.imageryLayers.remove(this.roadNetworkLayer);
            this.roadNetworkLayer = null;
          }
        }
      },
      toggleDayNight() {
        this.isNightMode = !this.isNightMode;
        if (this.isNightMode) {
          this.setNightMode();
        } else {
          this.setDayMode();
        }
      },
      setDayMode() {
        this.viewer.scene.globe.enableLighting = false;
        this.viewer.scene.globe.baseColor = Cesium.Color.WHITE;
        this.viewer.scene.backgroundColor = Cesium.Color.LIGHTBLUE.withAlpha(0.3);
        this.viewer.scene.sun.show = true;
        this.viewer.scene.moon.show = false;
      },
      setNightMode() {
        this.viewer.scene.globe.enableLighting = true;
        this.viewer.scene.globe.baseColor = Cesium.Color.BLACK.withAlpha(0.5);
        this.viewer.scene.backgroundColor = Cesium.Color.BLACK.withAlpha(0.7);
        this.viewer.scene.sun.show = false;
        this.viewer.scene.moon.show = true;
      },
      toggleGeologyPick() {
        this.isGeologyPickActive = !this.isGeologyPickActive;
        if (!this.isGeologyPickActive) {
          this.geologyData = null;
        }
      },
      callGeologyApi(longitude, latitude) {
        const apiUrl = `https://geologymine.fun/geotools/getGeologyType?lon=${longitude}&lat=${latitude}`;
        this.geologyData = null;
        axios.get(apiUrl)
          .then(response => {
            if (response.data.errcode === 0) {
              this.geologyData = response.data.body;
            } else {
              alert(`接口返回错误：${response.data.msg}`);
            }
          })
          .catch(error => {
            console.error("Geology API Error:", error);
            alert("无法获取地质数据，请检查网络或接口地址！");
          });
      },


      // 切换矩形绘制模式
      toggleDrawingRectangle() {
        // 切换状态
        this.isDrawingRectangle = !this.isDrawingRectangle;
        
        if (this.isDrawingRectangle) {

          this.exitPolygonDrawing();
          // 1. 关闭多边形模式
          this.isDrawingPolygon = false;
          this.clearPolygonDrawing();
          
          // 2. 清除地质数据
          this.clearGeologyData();

          this.clearPolygonDrawing();
          
          // 3. 清除矩形绘制状态
          this.rectangle = {
            minLon: null,
            minLat: null,
            maxLon: null,
            maxLat: null
          };
          
          // 4. 清除矩形临时实体
          if (this.tempPoint) {
            this.viewer.entities.remove(this.tempPoint);
            this.tempPoint = null;
          }
          
          if (this.rectangleEntity) {
            this.viewer.entities.remove(this.rectangleEntity);
            this.rectangleEntity = null;
          }
          
          // 5. 保存原始点击处理器
          if (!this.originalClickHandler) {
            this.originalClickHandler = this.handler.getInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
          }
          
          // 6. 移除多边形专用移动处理器（如果存在）
          this.handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
          
          // 7. 设置矩形专用移动处理器
          this.handler.setInputAction((movement) => {
            if (this.isDrawingRectangle && this.rectangle.minLon !== null) {
              const ray = this.viewer.scene.camera.getPickRay(movement.endPosition);
              const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene);
              
              if (cartesian) {
                const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
                const longitude = Cesium.Math.toDegrees(cartographic.longitude).toFixed(6);
                const latitude = Cesium.Math.toDegrees(cartographic.latitude).toFixed(6);
                
                this.rectangle.maxLon = parseFloat(longitude);
                this.rectangle.maxLat = parseFloat(latitude);
                
                this.updateRectangleOnMap();
              }
            }
          }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        } else {
          // 退出矩形模式
          this.handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
          
          if (this.tempPoint) {
            this.viewer.entities.remove(this.tempPoint);
            this.tempPoint = null;
          }
          
          if (this.rectangleEntity) {
            this.viewer.entities.remove(this.rectangleEntity);
            this.rectangleEntity = null;
          }
        }
      },

      drawRectangleOnMap() {
        let { minLon, minLat, maxLon, maxLat } = this.rectangle;
        const west = Math.min(minLon, maxLon);
        const east = Math.max(minLon, maxLon);
        const south = Math.min(minLat, maxLat);
        const north = Math.max(minLat, maxLat);

        if (west === east || south === north) {
          alert("无效的矩形区域，请选择不同的对角点");
          return;
        }

         // 设置查询类型
        this.currentQueryType = 'rectangle';

        // 保存当前矩形范围并显示查询框
        this.currentRectangle = { west, south, east, north };
        this.showKeywordModal = true;
        
        
        const apiUrl = "https://geologymine.fun/geotools/getGeologyFileByRectangle";
        const requestBody = { 
          minLon: west, 
          minLat: south, 
          maxLon: east, 
          maxLat: north 
        };
  
        axios.post(apiUrl, requestBody)
        .then(response => {
          if (response.data.errcode === 0) {
            const geojson = JSON.parse(response.data.body.geojsonFeaturecollection);
            this.addGeoJsonToCesium(response.data.body.geojsonFeaturecollection);

            // 提取所有 qduecd 并去重
            const qduecdSet = new Set();

            // 使用复合键(qduecd+tong)去重
            const strataMap = new Map();
            geojson.features.forEach(feature => {
              if (feature.properties && feature.properties.qduecd && feature.properties.tong) {
                const qduecd = feature.properties.qduecd;
                const tong = feature.properties.tong;
                const seq = parseInt(feature.properties.seq) || 0;
                const lower_age = feature.properties.lower_age;
                const upper_age = feature.properties.upper_age;
                
                const compositeKey = `${qduecd}_${tong}`;
                
                if (!strataMap.has(compositeKey) || seq < strataMap.get(compositeKey).seq) {
                  strataMap.set(compositeKey, {
                    qduecd: qduecd,
                    tong: tong,
                    seq: seq,
                    lower_age: lower_age,
                    upper_age: upper_age,
                    compositeKey: compositeKey
                  });
                }
              }
            });
            
            this.qduecdList = Array.from(strataMap.values())
              .sort((a, b) => a.seq - b.seq);
                }
        });
        
      },

      // // 修改后的addGeoJsonToCesium方法
      // addGeoJsonToCesium(geojson) {
      //   // 清除现有数据源
      //   if (this.currentDataSource) {
      //     this.viewer.dataSources.remove(this.currentDataSource);
      //   }

      //   // 创建新数据源
      //   Cesium.GeoJsonDataSource.load(geojson, {
      //     stroke: Cesium.Color.ORANGE,
      //     fill: Cesium.Color.YELLOW.withAlpha(0.1),
      //     strokeWidth: 2
      //   }).then(dataSource => {
      //     this.currentDataSource = dataSource;
      //     this.viewer.dataSources.add(dataSource);
          
      //     // 自动缩放到数据范围
      //     const entities = dataSource.entities.values;
      //     if (entities.length > 0) {
      //       this.viewer.flyTo(entities);
      //     }
      //   });
      // },

      // 第一次加载地质数据时不设置颜色
      async addGeoJsonToCesium(geojson) {
        if (!geojson) return null;

        // 清除现有数据源
        if (this.currentDataSourceRectangle) {
          this.viewer.dataSources.remove(this.currentDataSourceRectangle);
          this.currentDataSourceRectangle = null;
        }

        try {
          const geoJsonData = typeof geojson === 'string' ? JSON.parse(geojson) : geojson;
          
          if (!geoJsonData || !geoJsonData.features) {
            console.error("无效的GeoJSON数据");
            return null;
          }

          return Cesium.GeoJsonDataSource.load(geoJsonData, {
            stroke: Cesium.Color.WHITE,
            fill: Cesium.Color.BLUE.withAlpha(0.12),
            strokeWidth: 2,
            markerColor: Cesium.Color.BLUE,
            markerSize: 10
          }).then(dataSource => {
            this.currentDataSourceRectangle = dataSource;
            this.viewer.dataSources.add(dataSource);
            
            // 第一次加载时不设置颜色，保持默认样式
            
            return dataSource;
          }).catch(error => {
            console.error("加载GeoJSON时出错:", error);
            alert("加载GeoJSON数据失败！");
            return null;
          });
        } catch (error) {
          console.error("解析GeoJSON时出错:", error);
          alert("解析GeoJSON数据失败！");
          return null;
        }
      },

      // 添加选中的地质数据并应用颜色
      async addSelectedGeoJsonToCesium(geojson, colorCode) {
        if (!geojson) return null;

        try {
          const geoJsonData = typeof geojson === 'string' ? JSON.parse(geojson) : geojson;
          
          if (!geoJsonData || !geoJsonData.features) {
            console.error("无效的GeoJSON数据");
            return null;
          }

          // 创建数据源
          const dataSource = await Cesium.GeoJsonDataSource.load(geoJsonData);
          this.viewer.dataSources.add(dataSource);

          // 创建颜色对象
          const color = Cesium.Color.fromCssColorString(colorCode).withAlpha(0.92);
          
          // 遍历所有实体设置样式
          dataSource.entities.values.forEach(entity => {
            // 设置多边形样式
            if (entity.polygon) {
              entity.polygon.material = color;
              entity.polygon.outline = true;
              entity.polygon.outlineColor = Cesium.Color.BLACK.withAlpha(0.8);
              entity.polygon.outlineWidth = 1.5;
            }
            
            // 设置折线样式
            if (entity.polyline) {
              entity.polyline.material = color;
              entity.polyline.width = 2;
            }
            
            // 设置点样式（如果有）
            if (entity.point) {
              entity.point.color = color;
              entity.point.pixelSize = 8;
            }
          });

          // 强制刷新视图
          this.viewer.scene.requestRender();

          return dataSource;
        } catch (error) {
          console.error("解析GeoJSON时出错:", error);
          alert("解析GeoJSON数据失败！");
          return null;
        }
      },

      closeFeatureInfo() {
        this.selectedFeatureInfo = null;
      },
      closeElevationProfile() {
        this.showElevationProfile = false;
        if (this.elevationChart) {
          this.elevationChart.destroy();
          this.elevationChart = null;
        }
        if (this.polyline) {
          this.viewer.entities.remove(this.polyline);
          this.polyline = null;
        }
        if (this.rectangleEntity) {
          this.viewer.entities.remove(this.rectangleEntity);
          this.rectangleEntity = null;
        }
      },
      startDrawing() {
        if (this.currentDataSource) {
          this.viewer.dataSources.remove(this.currentDataSource);
          this.currentDataSource = null;
        }
        this.isDrawing = !this.isDrawing;
        if (this.isDrawing) {
          this.polylinePositions = [];
          if (this.polyline) {
            this.viewer.entities.remove(this.polyline);
          }
          this.polyline = this.viewer.entities.add({
            polyline: {
              positions: new Cesium.CallbackProperty(() => this.polylinePositions, false),
              width: 2,
              material: Cesium.Color.BLUE
            }
          });
        } else {
          this.interpolatePolylinePositions();
          this.generateElevationProfile();
        }
      },
      updatePolyline() {
        if (this.polyline) {
          this.polyline.polyline.positions = new Cesium.CallbackProperty(() => this.polylinePositions, false);
        }
      },

      // 切换页码
      changePage(page) {
        if (page < 1 || page > this.poiPagination.totalPages || page === this.poiPagination.currentPage) {
          return;
        }
        this.poiPagination.currentPage = page;
        this.fetchUserPois();

        // 滚动到列表顶部
        const container = this.$el.querySelector('.poi-list');
        if (container) container.scrollTop = 0;
      },
      interpolatePolylinePositions() {
        if (this.polylinePositions.length < 2) return;
  
        const newPositions = [];
        for (let i = 0; i < this.polylinePositions.length - 1; i++) {
          const startPoint = this.polylinePositions[i];
          const endPoint = this.polylinePositions[i + 1];
  
          const numInterpolations = 100;
          for (let j = 0; j <= numInterpolations; j++) {
            const t = j / numInterpolations;
            const interpolatedPoint = Cesium.Cartesian3.lerp(startPoint, endPoint, t, new Cesium.Cartesian3());
            newPositions.push(interpolatedPoint);
          }
        }
  
        this.polylinePositions = newPositions;
        this.updatePolyline();
      },
      resetToNorth() {
        this.viewer.camera.setView({
          orientation: {
            heading: 0,
            roll: 0.0
          }
        });
        
        const compassIcon = document.querySelector('.compass-button i');
        compassIcon.style.transform = 'rotate(360deg)';
        setTimeout(() => {
          compassIcon.style.transform = 'rotate(0deg)';
        }, 800);
      },
      async generateElevationProfile() {
        if (this.polylinePositions.length < 2) {
          alert("请至少绘制两个点以生成高程剖面图");
          return;
        }
  
        const samples = [];
        for (let i = 0; i < this.polylinePositions.length; i++) {
          const cartographic = Cesium.Cartographic.fromCartesian(this.polylinePositions[i]);
          const elevation = await this.viewer.scene.globe.getHeight(cartographic);
          samples.push({
            distance: i,
            elevation: elevation
          });
        }
  
        this.showElevationProfile = true;
        this.$nextTick(() => {
          this.renderElevationChart(samples);
        });
      },
      renderElevationChart(samples) {
        const ctx = this.$refs.elevationChart.getContext('2d');
        if (this.elevationChart) {
          this.elevationChart.destroy();
        }
        this.elevationChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: samples.map(s => s.distance),
            datasets: [{
              label: '高程 (m)',
              data: samples.map(s => s.elevation),
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1.2,
              fill: false,
              pointRadius: 0
            }]
          },
          options: {
            scales: {
              x: { title: { display: true, text: '距离 (点)' } },
              y: { title: { display: true, text: '高程 (m)' } }
            }
          }
        });
      }
    },
    beforeDestroy() {
      if (this.viewer) this.viewer.destroy();
      if (this.handler) this.handler.destroy();
      if (this.elevationChart) this.elevationChart.destroy();
      this.clearPoiMarkers();
      
      // 清除用户标记点
      if (this.userPoiMarker) {
        this.viewer.entities.remove(this.userPoiMarker);
      }
      
      // 清理原始处理器引用
      this.originalClickHandler = null;

      // 移除事件监听
      document.removeEventListener('click', this.handleGlobalClick);

      // 清除轨迹实体
      if (this.currentTrackEntity) {
        this.viewer.entities.remove(this.currentTrackEntity);
      }
    }
  };
  </script>
  
  <style>
  html, body, #cesiumContainer {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
  
  .cesium-container {
    position: relative;
    height: 100%;
    width: 100%;
  }
  
  /* 搜索框样式 */
  .search-container {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1000;
    width: 300px;
  }
  
  .search-box {
    display: flex;
    margin-bottom: 5px;
  }
  
  .search-input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px 0 0 4px;
    font-size: 14px;
  }
  
  .search-button {
    padding: 8px 15px;
    background-color: #0078d4;
    color: white;
    border: none;
    border-radius: 0 4px 4px 0;
    cursor: pointer;
    transition: background-color 0.3s;
  }
  
  .search-button:hover {
    background-color: #005bb5;
  }
  
  .poi-results {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-height: 500px;
    overflow-y: auto;
  }
  
  .poi-item {
    padding: 12px;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
  }
  
  .poi-item:hover {
    background-color: #f8f9fa;
    transform: translateX(2px);
  }
  
  .poi-name {
    font-weight: bold;
    margin-bottom: 8px;
    color: #333;
    font-size: 15px;
  }
  
  .poi-info {
    font-size: 13px;
    color: #666;
    margin-bottom: 8px;
  }
  
  .poi-address, .poi-tel, .poi-rating, .poi-level {
    margin-bottom: 4px;
    display: flex;
    align-items: center;
  }
  
  .poi-address i, .poi-tel i, .poi-rating i, .poi-level i {
    margin-right: 6px;
    color: #0078d4;
    width: 16px;
    text-align: center;
  }
  
  .poi-location {
    font-size: 12px;
    color: #888;
    margin-top: 4px;
  }
  
  .poi-photo-preview {
    margin-top: 8px;
  }
  
  .poi-photo-preview img {
    width: 100%;
    max-height: 120px;
    object-fit: cover;
    border-radius: 4px;
    cursor: zoom-in;
    transition: transform 0.2s;
  }
  
  .poi-photo-preview img:hover {
    transform: scale(1.02);
  }
  
  .poi-result-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
    padding: 12px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .poi-result-header h3 {
    margin: 0;
    font-size: 16px;
    color: #333;
  }
  
  .clear-button {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 5px;
    font-size: 16px;
  }
  
  .clear-button:hover {
    color: #666;
  }
  
  /* 原有按钮样式 */
  .geology-toggle-button {
    position: absolute;
    top: 10px;
    right: 80px;
    z-index: 1000;
    padding: 8px 12px;
    font-size: 14px;
    color: #fff;
    background-color: #0078d4;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .geology-toggle-button:hover {
    background-color: #005bb5;
  }
  
  .draw-button {
    position: absolute;
    top: 10px;
    right: 300px;
    z-index: 1000;
    padding: 8px 12px;
    font-size: 14px;
    color: #fff;
    background-color: #0078d4;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .draw-button:hover {
    background-color: #005bb5;
  }
  
  .day-night-toggle {
    position: absolute;
    bottom: 90px;
    right: 10px;
    z-index: 1000;
    width: 90px;
    padding: 8px 12px;
    font-size: 14px;
    color: #fff;
    background-color: #ea8eca;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .day-night-toggle:hover {
    background-color: #5a6268;
  }
  
  #cesiumContainer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
  
  .sidebar {
    position: absolute;
    bottom: 20px;
    left: 20px;
    width: 250px;
    height: 233px;
    background: rgba(255, 255, 255, 0.8);
    padding: 10px;
    border-radius: 5px;
    z-index: 1000;
    font-family: Arial, sans-serif;
    font-size: 14px;
  }
  
  .geology-data {
    margin-bottom: 20px;
  }
  
  .geology-data h3 {
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: bold;
  }
  
  .elevation-profile {
    position: absolute;
    top: 50px;
    left: 10px;
    background: rgba(255, 255, 255, 0.8);
    padding: 10px;
    border-radius: 5px;
    z-index: 1000;
    width: 400px;
    height: 300px;
    font-family: Arial, sans-serif;
  }
  
  .elevation-profile h3 {
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: bold;
  }
  
  .elevation-profile canvas {
    width: 100%;
    height: 100%;
  }
  
  .feature-info {
    position: absolute;
    top: 50px;
    left: 420px;
    background: rgba(255, 255, 255, 0.6);
    padding: 10px;
    border-radius: 5px;
    z-index: 1000;
    width: 300px;
    font-family: Arial, sans-serif;
  }
  
  .feature-info h3 {
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: bold;
  }
  
  .close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 5px 10px;
    font-size: 14px;
    color: #fff;
    background-color: #dc3545;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .close-button:hover {
    background-color: #a71d2a;
  }
  
  .draw-rectangle-button {
    position: absolute;
    top: 10px;
    right: 190px;
    z-index: 1000;
    padding: 8px 12px;
    font-size: 14px;
    color: #fff;
    background-color: #28a745;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .draw-rectangle-button:hover {
    background-color: #218838;
  }
  
  .compass-button {
    position: absolute;
    top: 11.6px;
    right: 400px;
    z-index: 1000;
    padding: 8px 12px;
    font-size: 16px;
    color: #fff;
    background-color: #17a2b8;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 40px;
    height: 33px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .compass-button:hover {
    background-color: #138496;
    transform: scale(1.1);
  }
  
  .compass-button i {
    transition: transform 0.3s ease;
  }
  
  .compass-button:hover i {
    transform: rotate(15deg);
  }
  
  .road-network-button {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 1000;
    padding: 8px 12px;
    font-size: 14px;
    width: 90px;
    color: #fff;
    background-color: #443ad2;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .road-network-button:hover {
    background-color: #58bfcf;
  }
  
  .terrain-toggle-button {
    position: absolute;
    bottom: 50px;
    right: 10px;
    z-index: 1000;
    padding: 8px 12px;
    width: 90px;
    font-size: 14px;
    color: #fff;
    background-color: #8a2be2;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .terrain-toggle-button:hover {
    background-color: #9932cc;
  }
  
  
  /* 用户头像样式 */
  .user-avatar-container {
    position: absolute;
    top: 8px;
    right: 20px;
    z-index: 1000;
  }
  
  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #0078d4;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 24px;
    position: relative;
    z-index: 1000; /* 确保在最上层 */
  }
  
  .user-avatar:hover {
    transform: scale(1.1);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  }
  
  .user-avatar img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
  
  .user-avatar.logged-in {
    background-color: #4CAF50;
  }
  
  .user-menu {
    position: absolute;
    right: 0;
    top: 50px;
    width: 230px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    z-index: 1001;
  }
  
  .user-info {
    padding: 12px;
    border-bottom: 1px solid #eee;
  }
  
  .username {
    font-weight: bold;
    margin-bottom: 4px;
  }
  
  .email {
    font-size: 12px;
    color: #666;
  }
  
  .menu-item {
    padding: 6px 8px;
    /* height: 8px; */
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .menu-item:hover {
    background-color: #931919;
  }
  
  /* 登录模态框样式 */
  .login-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }
  
  .login-content {
    background: white;
    border-radius: 8px;
    width: 350px;
    max-width: 90%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
  
  .login-header {
    padding: 16px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .login-header h3 {
    margin: 0;
    font-size: 18px;
  }
  
  .login-body {
    padding: 20px;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
  }
  
  .form-group input {
    width: 95%;
    padding: 8px 6px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
  }
  
  .login-button {
    width: 100%;
    padding: 10px;
    background-color: #0078d4;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.3s;
  }
  
  .login-button:hover {
    background-color: #005bb5;
  }
  
  /* 用户POI列表样式 */
  .poi-list-container {
    max-height: 300px;
    overflow-y: auto;
    margin-top: 10px;
    border-top: 1px solid #eee;
    padding-top: 10px;
  }
  
  .poi-list-container h4 {
    margin: 0 0 10px;
    padding: 0 12px;
    font-size: 14px;
    color: #333;
  }
  
  .poi-list {
    padding: 0 12px;
  }
  
  .poi-list .poi-item {
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .poi-list .poi-item:hover {
    background-color: #f8f8f8;
  }
  
  .poi-list .poi-name {
    font-weight: bold;
    font-size: 13px;
    margin-bottom: 4px;
  }
  
  .poi-list .poi-desc {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
  }
  
  .poi-list .poi-time {
    font-size: 11px;
    color: #999;
  }
  
  .loading, .no-data {
    text-align: center;
    padding: 10px;
    font-size: 12px;
    color: #666;
  }
  
  /* 调整用户菜单样式 */
  .user-menu {
    max-height: 80vh;
    overflow-y: auto;
  }
  
  .add-poi-button {
    position: absolute;
    top: 10.5px;
    right: 391px;
    z-index: 1000;
    padding: 8px 12px;
    font-size: 14px;
    color: #fff;
    background-color: #9c27b0;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .add-poi-button:hover {
    background-color: #7b1fa2;
  }
  
  .poi-form-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }
  
  .poi-form-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    max-width: 90%;
  }
  
  /* Add to your style section */
  .image-preview {
    position: relative;
    margin-top: 10px;
    display: inline-block;
  }
  
  .remove-image-button {
    position: absolute;
    top: -10px;
    right: -10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #ff4444;
    color: white;
    border: none;
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  
  .remove-image-button:hover {
    background: #cc0000;
  }
  
  /* Make file input look better */
  input[type="file"] {
    display: block;
    margin-top: 5px;
    padding: 5px;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: 100%;
  }

  /* 椭圆图片样式 */
  /* .poi-image-preview {
    margin-top: 8px;
    width: 100px;
    height: 80px;
    overflow-x: visible;
    overflow-y: visible;
    border-radius: 3px;
    border: 0px solid #53c364;
    cursor: pointer;
    transition: transform 0.2s;
  }
  .poi-image-preview img {
    width: 100%;
    height: 100%;
    object-fit: fill;
  } */
  /* 椭圆图片样式 */

  /* 矩形图片样式 */
  .poi-image-preview {
    margin-top: 8px;
  }

  .poi-image-preview img {
    max-width: 100%;
    max-height: 80px;
    border-radius: 4px;
    object-fit: cover;
  }
  /* 矩形图片样式 */

/* 
  .poi-image-preview:hover {
    transform: scale(1.02);
    box-shadow: 0 2px 8px rgba(125, 125, 124, 0.847);
  } */
  
  
  
  /* 调整POI项样式以适应图片 */
  .poi-list .poi-item {
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;  
    transition: all 0.2s;
  }
  
  .poi-list .poi-item:hover {
    background-color: #f8f9fa;
  }
  
  /* 图片加载中的样式 */
  .poi-image-loading {
    background: #f5f5f5;
    /* display: flex;
    align-items: center;
    justify-content: center; */
  }
  
  .poi-image-loading::after {
    content: "图片加载中...";
    color: #999;
    font-size: 12px;
  }
  .load-more-button {
  width: 100%;
  padding: 8px;
  margin-top: 8px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-button:hover {
  background-color: #e9e9e9;
  color: #333;
}

.loading {
  text-align: center;
  padding: 10px;
  color: #666;
  font-size: 12px;
}
/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 15px;
  padding: 10px 0;
  border-top: 1px solid #eee;
}

.loading {
  text-align: center;
  padding: 10px;
  color: #666;
  font-size: 12px;
}

.no-data {
  text-align: center;
  padding: 10px;
  color: #999;
  font-size: 12px;
}

/* 分页容器 */
.pagination-footer {
  margin-top: 10px;
  padding: 8px 0;
  border-top: 1px solid #eee;
}

/* 分页控件整体布局 */
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px; /* 按钮间距缩小 */
}

/* 分页按钮样式 */
.page-btn {
  margin: 0 1px; /* 减少外边距 */
  padding: 3px 8px; /* 减少内边距（原 5px 10px） */
  border: 1px solid #ddd;
  background-color: #fff;
  color: #333;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 26px; /* 缩小最小宽度（原 30px） */
  height: 26px; /* 固定高度 */
  font-size: 12px; /* 字体缩小（原 14px） */
  line-height: 1; /* 避免文字垂直偏移 */
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 悬停效果 */
.page-btn:hover:not(:disabled) {
  background-color: #f5f5f5;
}

/* 禁用状态 */
.page-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
}

/* 当前选中页样式 */
.page-btn.active {
  background-color: #0078d4;
  color: white;
  border-color: #0078d4;
}

/* 省略号按钮样式 */
.page-btn.ellipsis {
  cursor: default;
  background: transparent;
  border: none;
  min-width: 20px;
}

/* 分页信息文字（如“第1页/共5页”） */
.page-info {
  margin-left: 8px;
  font-size: 11px; /* 缩小字体（原 12px） */
  color: #666;
}
/* 表单操作按钮容器 */
.form-actions {
  display: flex;
  justify-content: center; /* 水平居中 */
  gap: 12px; /* 按钮间距 */
  margin-top: 16px;
}

/* 保存按钮（蓝色） */
.save-button {
  padding: 8px 20px;
  background-color: #0078d4; /* 微软蓝 */
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.save-button:hover {
  background-color: #005bb5; /* 深蓝色悬停 */
}

/* 取消按钮（红色） */
.cancel-button {
  padding: 8px 20px;
  background-color: #dc3545; /* Bootstrap 红色 */
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button:hover {
  background-color: #a71d2a; /* 深红色悬停 */
}
/* POI搜索框样式 */
.poi-search-box {
  position: relative;
  display: flex;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.poi-search-input {
  flex: 1;
  padding: 8px 12px;
  border: none;
  outline: none;
  font-size: 13px;
}

.track-search-box {
  position: relative;
  display: flex;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.track-search-input {
  flex: 1;
  padding: 8px 12px;
  border: none;
  outline: none;
  font-size: 11px;
}


.poi-search-button {
  padding: 0 12px;
  background-color: #f5f5f5;
  border: none;
  border-left: 1px solid #ddd;
  cursor: pointer;
  color: #666;
  transition: all 0.2s;
}

.poi-search-button:hover {
  background-color: #e9e9e9;
}

.poi-search-clear {
  padding: 0 12px;
  background-color: #f5f5f5;
  border: none;
  border-left: 1px solid #fdaeae;
  cursor: pointer;
  color: #999;
  transition: all 0.2s;
}

.poi-search-clear:hover {
  color: #666;
  background-color: #e9e9e9;
}
/* 切换表单链接 */
.switch-form {
  margin-top: 15px;
  text-align: center;
  font-size: 13px;
  color: #666;
}

.switch-form a {
  color: #0078d4;
  cursor: pointer;
  text-decoration: underline;
}

.switch-form a:hover {
  color: #005bb5;
}

/* 注册按钮样式 */
.register-button {
  width: 100%;
  padding: 10px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

/* 适用于复杂表单 */
.register-body {
  padding: 20px 30px; /* 增加左右内边距 */
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #444;
}

input::placeholder {
  color: #999;
  font-size: 13px;
}

/* 邮箱登录表单样式 */
.mail-login-body {
  padding: 20px;
}

.login-options {
  margin-top: 15px;
  text-align: center;
}

.login-options a {
  color: #0078d4;
  cursor: pointer;
  font-size: 13px;
}

.login-options a:hover {
  text-decoration: underline;
}

.captcha-group {
  position: relative;
}

.captcha-input {
  display: flex;
  gap: 10px;
}

.captcha-input input {
  flex: 1;
}

.send-captcha-btn {
  width: 120px;
  padding: 8px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.send-captcha-btn.disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.send-captcha-btn:not(.disabled):hover {
  background-color: #218838;
}

/* 坐标拾取按钮 */
.coordinate-pick-button {
  position: absolute;
  bottom: 133px;
  right: 10px; /* 根据现有按钮位置调整 */
  z-index: 1000;
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: #17a2b8;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  width: 90px; 
}

.coordinate-pick-button.active {
  background-color: #138496;
  box-shadow: 0 0 8px rgba(23, 162, 184, 0.5);
}

/* 坐标显示面板 */
.coordinate-panel {
  position: absolute;
  bottom: 60px;
  right: 130px;
  background: rgba(255, 255, 255, 0.9);
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 130px;
}

.coordinate-item {
  margin: 4px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.coordinate-item label {
  color: #666;
  font-size: 13px;
  margin-right: 8px;
}

.coordinate-item span {
  font-weight: bold;
  color: #333;
  font-size: 13px;
}

.copy-button {
  position: absolute;
  top: 1px;
  right: 5px;
  background: none;
  border: none;
  color: #666;
  padding: 4px;
  cursor: pointer;
  transition: color 0.2s;
}

.copy-button:hover {
  color: #333;
}







/* 定位触发按钮 */
.coordinate-trigger {
  position: absolute;
  top: 10px;
  left: 320px;
  z-index: 1000;
  width: 33px;
  height: 33px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
}

.coordinate-trigger:hover {
  transform: scale(1.1);
  background: #f8f9fa;
}

.coordinate-trigger i {
  color: #0078d4;
  font-size: 20px;
}

/* 坐标输入模态框 */
.coordinate-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.coordinate-input-box {
  background: white;
  border-radius: 8px;
  width: 320px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
}

.input-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.input-header h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
}

.close-btn:hover {
  color: #666;
}

.input-body {
  position: relative;
}

.input-body input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 8px;
  width: 295px;
}

.input-body input:focus {
  border-color: #0078d4;
  outline: none;
}

.error-msg {
  color: #dc3545;
  font-size: 12px;
  min-height: 18px;
  margin-bottom: 8px;
}

.confirm-btn {
  display: block;    /* 改为块级元素 */
  width: fit-content; /* 宽度自适应内容 */
  margin: 0 auto;    /* 水平居中 */
  padding: 10px 24px;
  background: #0078d4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
}

.confirm-btn:hover {
  background: #005bb5;
}


/* 删除标记点 */
.delete-btn {
  position: absolute;
  right: 1px;
  top: 27px;
  background: none;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  padding: 4px;
  z-index: 1;
  opacity: 0.7;
  transition: all 0.2s;
}

.delete-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.poi-item {
  position: relative;
  padding-right: 30px; /* 为删除按钮留出空间 */
}

.poi-header {
  position: relative;
  margin-bottom: 4px;
}







/* 新增地质查询模态框样式 */
.keyword-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.keyword-input-box {
  background: white;
  border-radius: 8px;
  padding: 20px;
  width: 300px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.keyword-input-box .input-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.keyword-input-box h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.keyword-input-box input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 12px;
}

.keyword-input-box .confirm-btn {
  width: 100%;
  padding: 10px;
  background: #0078d4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
}

.keyword-input-box .confirm-btn:hover {
  background: #005bb5;
}


/* 地层列表样式 */
.qduecd-sidebar {
  position: absolute;
  top: 60px;
  right: 20px;
  width: 250px;
  max-height: 60vh;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 12px;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.qduecd-list {
  max-height: 55vh;
  overflow-y: auto;
}

.qduecd-item {
  padding: 8px 12px;
  margin: 4px 0;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.qduecd-item:hover {
  background: #e9ecef;
  transform: translateX(4px);
}

/* 高亮样式 */
.qduecd-item.active {
  background: #0078d4;
  color: white;
  font-weight: bold;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transform: translateX(5px);
}

.qduecd-item.active:hover {
  background: #005bb5;
}

/* 新增轨迹相关样式 */
.poi-tabs {
  display: flex;
  border-bottom: 1px solid #eee;
  margin-bottom: 10px;
}

.tab-btn {
  flex: 1;
  padding: 8px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 13px;
  color: #666;
}

.tab-btn.active {
  border-bottom-color: #0078d4;
  color: #0078d4;
  font-weight: bold;
}

.track-list-container {
  padding: 0 12px;
}

.track-item {
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.track-item:hover {
  background-color: #f8f8f8;
}

.track-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.track-name {
  font-weight: bold;
  font-size: 14px;
  color: #000; /* 设置轨迹名称为黑色 */
}

.track-desc {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.add-track-btn-container {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.add-track-btn {
  width: 100%;
  padding: 8px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.add-track-btn:hover {
  background-color: #218838;
}

/* 轨迹上传模态框样式 */
.track-upload-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.track-upload-content {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.upload-header {
  padding: 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.upload-header h3 {
  margin: 0;
  font-size: 18px;
}

.upload-body {
  padding: 20px;
}

.file-preview {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-preview i {
  color: #0078d4;
}

.upload-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.upload-button {
  padding: 8px 16px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.upload-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.cancel-button {
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
/* 添加轨迹按钮样式 */
.add-track-control-button {
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 450px;
  z-index: 1000; /* 确保在最上层 */
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: #6f42c1;
  border: none;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.add-track-control-button:hover {
  background-color: #5a32a3;
}

/* 确保用户菜单在最上层 */
.user-menu {
  z-index: 1001;
  position: absolute;
  right: 0;
  top: 50px;
}

/* 确保模态框在最上层 */
.track-upload-modal, .login-modal {
  z-index: 2000;
}

.track-list-container {
  max-height: 300px;
  overflow-y: auto;
  margin-top: 10px;
  border-top: 1px solid #eee; 
  padding-top: 10px;
}

.track-list-content {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 10px;
}


/* 轨迹项样式 */
.track-item {
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.track-item:hover {
  background-color: #f8f8f8;
}

.track-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.track-name {
  font-weight: bold;
  font-size: 14px;
}

.track-desc {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.add-track-btn {
  width: 100%;
  padding: 8px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 10px;
}

.add-track-btn:hover {
  background-color: #218838;
}

/* 轨迹上传模态框样式 */
.track-upload-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.track-upload-content {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.upload-header {
  padding: 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.upload-header h3 {
  margin: 0;
  font-size: 18px;
}

.upload-body {
  padding: 20px;
}

.file-preview {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-preview i {
  color: #0078d4;
}

.upload-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.upload-button {
  padding: 8px 16px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.upload-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.cancel-button {
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
/* 添加轨迹按钮样式 */
.add-track-control-button {
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 450px;
  z-index: 1000; /* 确保在最上层 */
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: #6f42c1;
  border: none;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.add-track-control-button:hover {
  background-color: #5a32a3;
}

/* 确保用户菜单在最上层 */
.user-menu {
  z-index: 1001;
  position: absolute;
  right: 0;
  top: 50px;
}

/* 确保模态框在最上层 */
.track-upload-modal, .login-modal {
  z-index: 2000;
}

/* 新增轨迹时间样式 */
.track-time {
  font-size: 10px;
  color: #666;
  margin-bottom: 4px;
}

.time-row {
  display: flex;
  margin-bottom: 2px;
}

.time-label {
  width: 50px;
  color: #888;
}

.time-value {
  flex: 1;
}

/* 调整轨迹信息布局 */
.track-info {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #666;
}

.track-list-container h4 {
  color: #000; /* 设置标题文字为黑色 */
  font-size: 14px;
  margin: 0 0 10px;
}

/* 确保轨迹项有相对定位 */
.track-item {
  position: relative;
  padding-right: 30px; /* 为删除按钮留出空间 */
}

/* 删除按钮样式 */
.track-header .delete-btn {
  position: absolute;
  right: 1px;
  top: 1px;
  background: none;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  padding: 4px;
  z-index: 1;
  transition: all 0.2s;
}

.track-header .delete-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}


/* 新增缓冲区模态框样式 */
.buffer-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.buffer-content {
  background: white;
  border-radius: 8px;
  width: 320px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.buffer-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.buffer-header h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.buffer-body {
  padding: 20px;
}

.buffer-body .form-group {
  margin-bottom: 20px;
}

.buffer-body input {
  width: 96%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.buffer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.confirm-btn {
  padding: 8px 20px;
  background: #0078d4;
  color: white;
  border: none;
  height: 38px;
  width: 160px;
  left: 3px;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-btn {
  padding: 8px 27px;
  background: #f0f0f0;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-btn:hover {
  background: #005bb5;
}


/* 轨迹操作按钮容器 */
.track-actions {
  display: flex;
  gap: 5px;
}


/* 新增：缓冲区加载样式 */
.buffer-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

/* 修改：加载提示样式 */
.loading-spinner {
  text-align: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  width: 80%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center; /* 水平居中 */
}

.loading-spinner i {
  font-size: 36px;
  color: #0078d4;
  margin-bottom: 10px;
}

.loading-text {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
}

/* 新增：取消按钮容器 */
.loading-cancel-container {
  display: flex;
  justify-content: center; /* 水平居中 */
  width: 100%;
}

/* 新增：缓冲区加载样式 */
.buffer-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}


/* 修改：加载取消按钮样式 */
.loading-cancel-btn {
  padding: 8px 15px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.loading-cancel-btn:hover {
  background-color: #c82333;
}

.loading-cancel-btn i {
  font-size: 14px;
  color: white;
  margin: 0;
}

/* 新增地质详情按钮样式 */
.geology-detail-button {
  position: absolute;
  top: 10px;
  right: 560px; /* 根据现有按钮位置调整 */
  z-index: 1000;
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: #8a2be2; /* 紫色 */
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.geology-detail-button:hover {
  background-color: #7a1fd2;
}


/* 图片模态框样式 */
.image-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3000;
}

.image-modal-content {
    position: relative;
    max-width: 90%;
    max-height: 90%;
    background: white;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
}

.modal-image {
    max-width: 100%;
    max-height: 80vh;
    display: block;
    margin: 0 auto;
}

.image-info {
    margin-top: 10px;
    font-size: 14px;
    color: #333;
}

.close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    cursor: pointer;
}

/* 新增地质详情按钮激活状态样式 */
.geology-detail-button.active {
  background-color: #5a32a3;
  box-shadow: 0 0 8px rgba(90, 50, 163, 0.5);
}


/* 清除控制面板样式 */
.clear-control-panel {
  position: absolute;
  top: 1.2px;
  left: 360px; /* 移动到左上角，避免与用户菜单冲突 */
  z-index: 1002; /* 提高层级确保可见 */
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clear-btn {
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  transition: all 0.2s;
  min-width: 120px;
}

.clear-marker-btn {
  background-color: #ff4444;
  color: white;
}

.clear-marker-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.clear-track-btn {
  background-color: #ff8800;
  color: white;
}

.clear-track-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.clear-area-btn {
  background-color: #3429ca;
  color: white;
}

.clear-area-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
}


.clear-area-btn:not(:disabled):hover {
  background-color: #1f45a4;
  transform: scale(1.05);
}


.clear-marker-btn:not(:disabled):hover {
  background-color: #cc0000;
  transform: scale(1.05);
}

.clear-track-btn:not(:disabled):hover {
  background-color: #cc6600;
  transform: scale(1.05);
}

/* 地层信息模态框样式 */
.stratum-info-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.stratum-info-content {
  background: white;
  border-radius: 10px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.stratum-info-header {
  padding: 15px 20px;
  background: #0078d4;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stratum-info-header h4 {
  margin: 0;
  font-size: 18px;
}

.stratum-info-header .close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

.stratum-info-body {
  padding: 25px 20px;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
}

.stratum-info-body p {
  margin: 0;
}

.age-highlight {
  font-weight: bold;
  color: #e74c3c;
  font-size: 18px;
}

/* 地质信息卡片样式 */
.stratum-info-card {
  position: absolute;
  top: 80px;
  left: 20px;
  width: 280px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.3s ease;
  transform: translateX(-320px);
  opacity: 0;
  visibility: hidden;
}

.stratum-info-card.active {
  transform: translateX(0);
  opacity: 1;
  visibility: visible;
}

.card-header {
  padding: 12px 16px;
  background: linear-gradient(135deg, #1a6dcc, #0d4a9e);
  border-radius: 10px 10px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  font-size: 16px;
  color: white;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.card-body {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.stratum-header {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.stratum-name {
  font-size: 20px;
  font-weight: bold;
  color: #0d4a9e;
  margin-bottom: 5px;
}

.stratum-tong {
  font-size: 16px;
  color: #e74c3c;
  font-weight: 500;
}

.stratum-period {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.period-item {
  text-align: center;
  flex: 1;
}

.period-label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 5px;
}

.period-value {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #1071F1;
}

.stratum-description {
  font-size: 14px;
  line-height: 1.6;
  color: #444;
}

.stratum-description .highlight {
  color: #e74c3c;
  font-weight: 500;
}

.empty-state {
  text-align: center;
  padding: 30px 0;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #d0d0d0;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* 地层项样式 */
.qduecd-item {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  margin: 4px 0;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

/* 地层名称 - 大字体加粗 */
.stratum-name {
  font-size: 16px;   /* 增大字体 */
  font-weight: bold; /* 加粗 */
  line-height: 1.3;
}

/* 地层年代 - 略小字体 */
.stratum-tong {
  font-size: 13px;   /* 略小字体 */
  color: #ED2020;    /* 保持红色 */
  margin-top: 3px;
}

/* 高亮样式 */
.qduecd-item.active {
  background: #0078d4;
  color: white;
}

.qduecd-item.active .stratum-name {
  color: white;
}

.qduecd-item.active .stratum-tong {
  color: #ffd700; /* 金色更显眼 */
}


/* 新增多边形按钮样式 */
.draw-polygon-button {
  position: absolute;
  top: 50px;
  right: 70px; /* 放置在矩形按钮右侧 */
  z-index: 1000;
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: #17a2b8; /* 青色 */
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.draw-polygon-button:hover {
  background-color: #138496;
}


/* 在样式部分添加或修改以下规则 */

/* 矩形按钮样式 */
.draw-rectangle-button {
  position: absolute;
  top: 12px;
  right: 190px; /* 调整位置 */
  z-index: 1000;
  width: 33px;
  height: 33px;
  padding: 0;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.draw-rectangle-button:hover {
  background-color: #218838;
  transform: scale(1.1);
}

/* 多边形按钮样式 */
.draw-polygon-button {
  position: absolute;
  top: 12px;
  right: 140px; /* 调整位置 */
  z-index: 1000;
  width: 33px;
  height: 33px;
  padding: 0;
  background-color: #17a2b8;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.draw-polygon-button:hover {
  background-color: #138496;
  transform: scale(1.1);
}

.draw-polygon-button.active {
  background-color: #138496;
  box-shadow: 0 0 8px rgba(23, 162, 184, 0.5);
}



.profile-button {
  position: absolute;
  top: 12px;
  right: 240px; /* 调整位置，确保在矩形按钮左侧 */
  z-index: 1000;
  width: 33px;
  height: 33px;
  padding: 0;
  background-color: #a06808;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.profile-button:hover {
  background-color: #ed8c0e;
  transform: scale(1.1);
}

.profile-button i {
  font-size: 16px;
}
.geology-pick-icon-button {
  position: absolute;
  top: 12px;
  right: 90px; /* 调整位置 */
  z-index: 1000;
  width: 33px;
  height: 33px;
  padding: 0;
  background-color: #63ceb0;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.geology-pick-icon-button:hover {
  background-color: rgb(66, 203, 114);
  transform: scale(1.1);
}

.geology-pick-icon-button.active {
  background-color: #4cdc75;
  box-shadow: 0 0 8px rgba(134, 230, 155, 0.849);
}

.geology-pick-icon-button i {
  font-size: 16px;
}


/* 地质详情图标按钮 */
.geology-detail-icon-button {
  position: absolute;
  top: 12px;
  right: 290px; /* 调整位置 */
  z-index: 1000;
  width: 33px;
  height: 33px;
  padding: 0;
  background-color: #e83030;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.geology-detail-icon-button:hover {
  background-color: #ef1e3d;
  transform: scale(1.1);
}

.geology-detail-icon-button.active {
  background-color: #c21238;
  box-shadow: 0 0 8px rgba(90, 50, 163, 0.5);
}

.geology-detail-icon-button i {
  font-size: 16px;
}

/* 添加标记点图标按钮 */
.add-poi-icon-button {
  position: absolute;
  top: 12px;
  right: 340px; /* 调整位置 */
  z-index: 1000;
  width: 33px;
  height: 33px;
  padding: 0;
  background-color: #9c27b0; /* 紫色，与之前添加标记点按钮颜色一致 */
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.add-poi-icon-button:hover {
  background-color: #7b1fa2;
  transform: scale(1.1);
}

.add-poi-icon-button i {
  font-size: 16px;
}

/* 新增区域选项卡样式 - 调整左侧边距 */
.area-list-container {
  max-height: 300px;
  overflow-y: auto;
  margin-top: 10px;
  border-top: 1px solid #eee;
  padding: 10px 15px; /* 增加左右内边距 */
  box-sizing: border-box; /* 确保内边距不影响整体宽度 */
}

.area-search-box {
  position: relative;
  display: flex;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.area-search-input {
  flex: 1;
  padding: 8px 8px;
  border: none;
  outline: none;
  font-size: 11px;
}

.delete-btn {
  background: none;
  top: 1px;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.3s;
  z-index: 10; /* 确保按钮在点击区域之上 */
}

.delete-btn:hover {
  background: rgba(255, 107, 107, 0.1);
  color: #da5757;
}

.area-item {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s;
}

.area-item:hover {
  background-color: #f9f9f9;
}

.area-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.area-name {
  font-weight: bold;
  font-size: 14px;
  color: #2c3e50;
  margin-left: 5px; /* 增加名称左侧间距 */
}

.area-desc {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  padding-left: 5px; /* 增加描述左侧间距 */
}

.area-info {
  font-size: 11px;
  color: #7f8c8d;
  display: flex;
  padding-left: 5px; /* 增加信息左侧间距 */
}

.info-label {
  width: 50px;
  color: #95a5a6;
}

.info-value {
  flex: 1;
}

.add-area-btn {
  width: 100%;
  padding: 8px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 10px;
  margin-left: 5px; /* 增加按钮左侧间距 */
}

.add-area-btn:hover {
  background-color: #2980b9;
}

/* 区域上传模态框样式 */
.area-upload-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.area-upload-content {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.polygon-list-title {
  color: #333;
  font-size: 14px;
  font-weight: 600;
  margin: 12px 0 8px;
  padding-left: 5px; /* 增加标题左侧间距 */
}

.total-count {
  color: #666;
  font-weight: normal;
  font-size: 14px;
}



/* 区域操作按钮容器 */
.area-actions {
  display: flex;
  gap: 12px; /* 按钮间距 */
}

/* 信息按钮样式 */
.info-btn {
  position: absolute;
  right: 22px;
  top: 1px;
  background: none;
  border: none;
  color: #17a2b8;
  cursor: pointer;
  padding: 4px;
  z-index: 1;
  opacity: 0.7;
  width: 24px;
  height: 24px;
  transition: all 0.2s;
}

.info-btn:hover {
  color: #1c7ed6;
}

/* 区域项悬停效果 */
.area-item:hover {
  background-color: #f9f9f9;
}


/* 缓冲区按钮样式 */
.bufferinfo-btn {
  position: absolute;
  right: 18px;
  top: 1px;
  background: none;
  border: none;
  color: #17a2b8;
  cursor: pointer;
  padding: 4px;
  z-index: 1;
  opacity: 0.7;
  width: 24px;
  height: 24px;
  transition: all 0.2s;
}
  </style>